// ============================================================================
// FSA NOTE MODAL — MAIN NOTE PANE
// ============================================================================
// Per-class rows. Closing actual (TB-derived, read-only) + Closing rounded
// (editable, tolerance-checked) + Opening actual / rounded for PY column.
// Line items expand under each class row (read-only stubs in stage 1; full
// add/edit form follows in next iteration).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ActionMenu, type ActionMenuItem } from 'components/ui';
import { NumericInput } from '../shared/NumericInput';
import { autoRound as sharedAutoRound } from '../shared/rounding';
import {
  CustomClassChip,
  // FSA-2.4 N1 — LedgerDriftChip, ReconciledChip, DriftChip retired
  // from this file. Line-item + class-level + within-class drift now
  // render as muted amber dots (silence = good when reconciled) so
  // note-level drift (Deviations panel + sidebar Drift group) stays
  // primary. Components remain in RowBadges for other consumers.
} from '../shared/RowBadges';
import MultiColumnClassTable from './multi-column/MultiColumnClassTable';
import AgeingSectionBand from './multi-column/AgeingSectionBand';
import MatrixPyGrid from './multi-column/MatrixPyGrid';
import DepCalcSuggestionStrip, { DepCalcDtStrip } from './multi-column/DepCalcSuggestionStrip';
import SignSweepSection from './multi-column/SignSweepSection';
import { parseSpec as parseMultiColumnSpec } from './multi-column/noteClassDataResolver';
import ClassLabelEditor from './shared/ClassLabelEditor';
import NoteDeviationPanel, { type NoteDeviationItem } from './shared/NoteDeviationPanel';
import { evalClassSubtotal } from '../shared/subtotalFormula';
import { sumColumnTotals } from '../shared/columnTotals';
import SharedLineItemEditor, {
  blankLineItemDraft,
  type LineItemDraft as SharedLineItemDraft,
  type LineItemEditorLedger,
} from './shared/LineItemEditor';
import type { FormatColumnSpec } from 'api/types/fsa/template.types';
import type { ID } from 'api/types';
import type {
  NoteHydrate,
  NoteWorkspaceData,
  NoteClassData,
  NoteClassLive,
  NoteLineItem,
  NoteGroupingDimension,
  NoteCrossNoteTarget,
  NoteCrossNoteConfig,
  NoteIncomingCrossNote,
} from 'api/types/fsa/noteData.types';

interface Props {
  hydrate: NoteHydrate;
  /** Owning engagement id — used by the Owners' Capital "Add Partners" action. */
  engagementId: ID;
  workspace: NoteWorkspaceData;
  setClassField: <K extends keyof NoteClassData>(
    classCode: string,
    field: K,
    value: NoteClassData[K],
  ) => void;
  /**
   * Atomically updates a MANUAL_DISCLOSURE class's actual amount AND
   * recomputes its rounded companions. setClassField alone only writes
   * one field — auditors editing manual actuals expect rounded to follow.
   */
  setClassActual: (
    classCode: string,
    field: 'amount_actual' | 'prior_amount_actual',
    value: number,
  ) => void;
  setLineItems: (classCode: string, lineItems: NoteLineItem[]) => void;
  setLineItemRounded: (
    classCode: string,
    localId: string,
    field: 'amount_rounded' | 'prior_amount_rounded',
    value: number,
  ) => void;
  resetRoundingFromActuals: () => void;
  /**
   * Deliverable Note-level free text — saved to workspace.note_remark and
   * rendered as a printed footnote in the PDF (Schedule III style). Distinct
   * from the PRIVATE reviewer notes_text surface in the Auditor Notes pane.
   */
  setNoteRemark: (text: string) => void;
  /** Toggles workspace.class_data[code].hide_in_print. */
  setClassHideInPrint: (classCode: string, hide: boolean) => void;
  /** One-click helper — hides every class whose CY+PY actual/rounded are all zero. */
  autoHideEmptyClasses: () => void;
  /** Adds an engagement-time custom class at sibling level. Caller computes display_order. */
  addCustomClass: (name: string, displayOrder: number) => void;
  /** Renames a custom class identified by local_id. */
  renameCustomClass: (localId: string, name: string) => void;
  /** Deletes a custom class (and its class_data) by local_id. */
  deleteCustomClass: (localId: string) => void;
  /**
   * Per-engagement template-class label override. Pass null/'' to clear.
   * Auditor's label replaces the template's seed label everywhere it
   * renders (modal + PDF), but the seed stays canonical in the audit
   * trail.
   */
  setClassLabelOverride: (classCode: string, label: string | null) => void;
  /**
   * FSA-2.2 saved-as-primary: pulls the latest live TB snapshot for a
   * single class into class_data (actual + ledger list + fresh rounded).
   * Dirties the workspace so the Save button activates.
   */
  applyLedgerSnapshot: (classCode: string) => void;
  /** Same as applyLedgerSnapshot but for every class in one pass. */
  applyAllLedgerSnapshots: () => void;
  /**
   * "Recalculate Totals" — re-derives every class's amount_actual /
   * amount_rounded (CY + PY + auto-rounded companions) from its current
   * line_items sum. Doesn't touch ledgers, doesn't touch line items
   * themselves; only the class-level totals are rebuilt. Useful after
   * inline rounded-amount edits, or to fix a previously-saved Note where
   * the persisted class total drifted from its line-item sum.
   */
  recalculateAllTotals: () => void;
  /**
   * FSA-2.2 Wave 3 (A): pull the current live cross-note aggregator
   * into every cross_note-mode line item across class_data. Adds new
   * ones, refreshes amounts on matching ones (matched by local_id =
   * `xn::<source_schedule>::<source_class>::<source_line|class>`), and
   * drops orphans whose source no longer appears in the aggregator.
   * Dirties the workspace so Save activates.
   */
  applyAllCrossNoteSnapshots: () => void;
  /**
   * FSA-2.2 Wave 1 (#1): set a class-level dimension_values entry.
   * Used by the class-direct action menu's "Set <dimension.label>: …"
   * items so a class with no line items can be tagged Secured /
   * Unsecured (or any equivalent dimension). Pass `value = null` to
   * clear the dimension for this class. Idempotent — writes through
   * setClassField semantics so dirty tracking + Save button activate.
   */
  setClassDimensionValue: (classCode: string, dimCode: string, value: string | null) => void;
  /**
   * Multi-column notes only: persists one column's Previous Year Total
   * row value (or its `__rounded` companion when columnCode ends with
   * the suffix). Used by the multi-col footer's editable PY row. Pass
   * `null` to clear the entry.
   */
  setPriorYearTotal: (columnCode: string, value: number | null) => void;
  /**
   * Multi-column notes only: replaces the persisted per-column Total
   * row map. UI computes Σ class.column_values per column and pushes
   * the whole dict here. Persisted to workspace.column_totals; PDF
   * renderer reads it as-is.
   */
  setColumnTotals: (totals: Record<string, number>) => void;
  /**
   * FSA ICAI NCE — re-runs the Owners' Capital appropriation placement
   * (profit share = P&L profit × the row's Share %) after an inline
   * Share % edit. No-op on notes without appropriation routing; optional.
   */
  recomputeAppropriations?: () => void;
  readOnly: boolean;
  /**
   * Note-Modal-level display mode toggle — controls whether amount columns
   * render their actual or rounded values across the WHOLE Note (main
   * class table + disclosure grids below). Lifted out of this pane so the
   * DisclosuresPane below can render in the same mode. Persisted to
   * localStorage by the modal so the auditor's choice survives re-opens.
   */
  displayMode: DisplayMode;
  setDisplayMode: (mode: DisplayMode) => void;
  /**
   * Framing 1 auto-save: structural Add actions (Add Custom Class / Save
   * Line Item) call this AFTER mutating the workspace via setLineItems /
   * addCustomClass. The Note Modal owns a useEffect that runs the actual
   * save on the next render — by then React has committed the workspace
   * change, so the save reads the freshest state. 409 concurrency is
   * handled in the modal (refetch + retry-once + friendly toast).
   *
   * Optional: panes used in read-only contexts (PDF preview render) can
   * omit it; structural actions are also disabled via `readOnly`.
   */
  requestAutoSave?: () => void;
  /**
   * FSA-2.2 — refetches the hydrate. Used by the SignSweepSection after
   * the auditor toggles a per-ledger exclude flag so the auto-sweep
   * recomputes against the fresh TB state. Optional; the section
   * degrades gracefully when omitted.
   */
  refetchHydrate?: () => void;
}

// Drift tolerance for line-items → class-total reconciliation (in actuals).
// Distinct from the engagement-level rounding tolerance (which governs the
// gap between user-edited rounded values and auto-rounded values). Here we
// just want to absorb floating-point noise — line items sum to class total
// by audit contract.
const RECON_TOLERANCE_ACTUAL = 0.01;

// Roles that take auditor-typed input (and therefore: editable amount
// inputs in the row, no TB-ledger source pill, no "Update from Ledger"
// action). MANUAL_DISCLOSURE = classes whose amounts are entered by the
// auditor (ageing-matrix sub-classes, EPS computation rows). MEMO_ONLY
// = memorandum classes that show in the Note but are excluded from the
// Note's total (Authorised Capital, EPS computation rows). Both flavours
// need editable cells when the class isn't itself a computed one.
const isManualClass = (role?: string | null): boolean => {
  if (!role) return false;
  const r = role.toUpperCase();
  return r === 'MANUAL_DISCLOSURE' || r === 'MEMO_ONLY';
};

const isMemoOnlyClass = (role?: string | null): boolean =>
  !!role && role.toUpperCase() === 'MEMO_ONLY';

/**
 * Returns a short human-readable summary of a class's computation_rule
 * for use in the row meta-hint (e.g. "computed: PL XV" or "computed:
 * A ÷ B"). Returns null when the class isn't computed or the rule
 * is unparseable. Display-only; the backend is authoritative for
 * the actual computed value.
 */
/**
 * Parses the meta_data JSONB string from either the schedule or a
 * class and returns the `display_options` sub-object. Used for two
 * Note-level UX flags (FSA-2.2+):
 *   - schedule.display_options.show_total = false → suppress the
 *     bottom Total row in the Note table (Note 35 EPS, where Σ of
 *     mixed-unit classes is meaningless).
 *   - class.display_options.rounding_exempt = true → display the
 *     class's amount raw regardless of the engagement rounding factor
 *     (Note 35 share counts + per-share Rs values).
 * Null/empty/unparseable → returns null (defaults apply).
 */
const parseDisplayOptions = (json?: string | null): {
  showTotal?: boolean;
  roundingExempt?: boolean;
  pyLayout?: string;
  sectionLayout?: boolean;
} | null => {
  if (!json || json === '{}') return null;
  try {
    const r = JSON.parse(json) as {
      display_options?: { show_total?: boolean; rounding_exempt?: boolean; py_layout?: string; section_layout?: boolean };
    };
    if (!r.display_options) return null;
    return {
      showTotal: r.display_options.show_total,
      roundingExempt: r.display_options.rounding_exempt,
      pyLayout: r.display_options.py_layout,
      sectionLayout: r.display_options.section_layout === true,
    };
  } catch {
    return null;
  }
};

const computedRuleHint = (json?: string | null): string | null => {
  if (!json || json === '{}') return null;
  try {
    const r = JSON.parse(json) as {
      type?: string;
      report_code?: string;
      line_code?: string;
      numerator_class?: string;
      denominator_class?: string;
      operands?: string[];
    };
    const t = (r.type || '').toUpperCase();
    if (t === 'STATEMENT_VALUE' && r.report_code && r.line_code) {
      return `computed from ${r.report_code} · ${r.line_code}`;
    }
    if (t === 'DIVIDE' && r.numerator_class && r.denominator_class) {
      return `computed: ${r.numerator_class} ÷ ${r.denominator_class}`;
    }
    if (t === 'SUM' && Array.isArray(r.operands) && r.operands.length > 0) {
      return `computed: ${r.operands.join(' + ')}`;
    }
    return 'computed';
  } catch {
    return 'computed';
  }
};

// NumericInput, formatNumber, and autoRound previously lived inline here.
// They were extracted into ./shared/NumericInput.tsx + ./shared/rounding.ts
// so the disclosure dual-input editor can reuse the same components.
// `autoRound` is imported as `sharedAutoRound` and re-aliased below so the
// rest of this file's call sites stay readable.
const autoRound = sharedAutoRound;

export type DisplayMode = 'actual' | 'rounded';

const MainNotePane: React.FC<Props> = ({
  hydrate,
  engagementId,
  workspace,
  setClassField,
  setClassActual,
  setLineItems,
  setLineItemRounded,
  resetRoundingFromActuals,
  setNoteRemark,
  setClassHideInPrint,
  autoHideEmptyClasses,
  addCustomClass,
  renameCustomClass,
  deleteCustomClass,
  setClassLabelOverride,
  applyLedgerSnapshot,
  applyAllLedgerSnapshots,
  recalculateAllTotals,
  applyAllCrossNoteSnapshots,
  setClassDimensionValue,
  setPriorYearTotal,
  setColumnTotals,
  recomputeAppropriations,
  readOnly,
  displayMode,
  setDisplayMode,
  requestAutoSave,
  refetchHydrate,
}) => {

  // FSA-2.2 Option A (Phase 2): if the schedule's column_spec declares
  // an is_total column, this Note renders the multi-column class table
  // (PPE 10-col movement, Intangibles, future Partnerships) instead of
  // the standard CY/PY table. Parse once per hydrate; cheap and stable.
  const multiColumnSpec = useMemo(() => {
    if (!hydrate.columnSpecJson) return null;
    try {
      const parsed = JSON.parse(hydrate.columnSpecJson) as FormatColumnSpec[];
      const view = parseMultiColumnSpec(parsed);
      return view.isMultiColumn ? { columns: parsed, view } : null;
    } catch {
      return null;
    }
  }, [hydrate.columnSpecJson]);
  const isMultiColumn = multiColumnSpec != null;

  // Progressive disclosure for free-text editors. Each editor auto-shows
  // when the persisted value is non-empty; otherwise the user reveals it
  // via the "+ Add Text" affordance and the local Set tracks the manual
  // toggle. Reset to a fresh Set on workspace swap (handled by hydrate
  // re-mount).
  const [noteTextOpen, setNoteTextOpen] = useState<boolean>(false);
  const [classTextOpen, setClassTextOpen] = useState<Set<string>>(() => new Set());
  const [lineItemTextOpen, setLineItemTextOpen] = useState<Set<string>>(() => new Set());

  const noteTextVisible = noteTextOpen || !!(workspace.note_remark && workspace.note_remark.trim());

  const isClassTextVisible = useCallback(
    (classCode: string): boolean => {
      if (classTextOpen.has(classCode)) return true;
      const r = workspace.class_data[classCode]?.remarks;
      return !!(r && r.trim());
    },
    [classTextOpen, workspace.class_data],
  );

  const isLineItemTextVisible = useCallback(
    (classCode: string, localId: string): boolean => {
      const key = `${classCode}::${localId}`;
      if (lineItemTextOpen.has(key)) return true;
      const li = workspace.class_data[classCode]?.line_items.find(x => x.local_id === localId);
      return !!(li?.remarks && li.remarks.trim());
    },
    [lineItemTextOpen, workspace.class_data],
  );

  const handleRevealNoteText = useCallback(() => setNoteTextOpen(true), []);

  const handleRevealClassText = useCallback((classCode: string) => {
    setClassTextOpen(prev => {
      const next = new Set(prev);
      next.add(classCode);
      return next;
    });
  }, []);

  const handleRevealLineItemText = useCallback((classCode: string, localId: string) => {
    setLineItemTextOpen(prev => {
      const next = new Set(prev);
      next.add(`${classCode}::${localId}`);
      return next;
    });
  }, []);

  /**
   * Updates a single line item's free-text remark without disturbing its
   * amount values. Wraps setLineItems with an immutable swap of the one
   * line item. Used by the inline line-item remark editor.
   */
  const setLineItemRemark = useCallback(
    (classCode: string, localId: string, text: string) => {
      const cd = workspace.class_data[classCode];
      if (!cd) return;
      const next = cd.line_items.map(li =>
        li.local_id === localId ? { ...li, remarks: text } : li,
      );
      setLineItems(classCode, next);
    },
    [workspace.class_data, setLineItems],
  );

  // Custom-class authoring — inline form state. Picking "Add Custom
  // Class" from the header + Add menu reveals an inline form with
  // Position + Name fields. The Position picker offers "At start",
  // "After <class>" for every current top-level entry, and "At end".
  // The form computes a decimal display_order so the new class lands
  // exactly where the auditor placed it, without renumbering siblings.
  // Rename uses the same form, mode = 'rename', position fields hidden.
  const [customClassDraft, setCustomClassDraft] = useState<
    | null
    // anchor: '' = "At start", localId-or-classId string = "After X"
    | { mode: 'add'; anchor: string; name: string }
    // Rename a custom class (auditor-created bucket) — name only.
    | { mode: 'rename'; localId: string; name: string }
    // Rename a TEMPLATE class — saves a per-engagement override that
    // replaces the seed label in modal + PDF, leaving the seed intact.
    | { mode: 'rename-template'; classCode: string; name: string }
  >(null);

  const handleAddCustomClass = useCallback(() => {
    // Default anchor = the last existing top-level entry → places new
    // class at the end. Auditor can change before confirming.
    setCustomClassDraft({
      mode: 'add',
      anchor: 'END',
      name: '',
    });
  }, []);

  const handleStartRenameCustom = useCallback((localId: string, currentName: string) => {
    setCustomClassDraft({ mode: 'rename', localId, name: currentName });
  }, []);

  const handleStartRenameTemplate = useCallback((classCode: string, currentLabel: string) => {
    setCustomClassDraft({ mode: 'rename-template', classCode, name: currentLabel });
  }, []);

  const handleCancelCustomClass = useCallback(() => {
    setCustomClassDraft(null);
  }, []);

  // Lookup set of class codes (= local_ids) that came from added_classes
  // — used to decide whether a row in the table is a custom class so
  // the action menu can offer Rename/Delete and the visual chip can be
  // shown. Recomputed when the added_classes list changes.
  const customClassCodes = useMemo(
    () => new Set((workspace.added_classes ?? []).map(a => a.local_id)),
    [workspace.added_classes],
  );

  // Per-engagement rename overrides for template classes. Helpers
  // resolve the effective display label + tell us whether an override
  // is active so the UI can show a "renamed" badge and offer a reset.
  const labelOverrides = workspace.class_label_overrides ?? {};
  // Substitutes {{class:CODE}} tokens in a class label with the
  // current workspace value for the named class's amount_actual.
  // Note 35 EPS uses this to render "par value of Rs. <face_value>
  // per share" dynamically from the auditor's typed N35_FACE_VALUE.
  // Whole numbers render without a decimal (Rs. 10 not Rs. 10.00);
  // fractional values get 2dp.
  const substitutePlaceholders = useCallback((label: string): string => {
    if (!label.includes('{{class:')) return label;
    return label.replace(/\{\{class:([A-Z0-9_]+)\}\}/g, (_match, code: string) => {
      const v = workspace.class_data?.[code]?.amount_actual;
      if (v == null) return '_';
      const isWhole = Math.abs(v - Math.round(v)) < 0.005;
      return isWhole ? String(Math.round(v)) : v.toFixed(2);
    });
  }, [workspace.class_data]);
  const resolveClassLabel = useCallback(
    (classCode: string, templateLabel: string): string =>
      substitutePlaceholders(labelOverrides[classCode]?.trim() || templateLabel),
    [labelOverrides, substitutePlaceholders],
  );
  const hasLabelOverride = useCallback(
    (classCode: string): boolean =>
      typeof labelOverrides[classCode] === 'string' && !!labelOverrides[classCode].trim(),
    [labelOverrides],
  );

  // FSA-2.2 step 4b — live TB-side breakdown for custom classes, keyed
  // by local_id. Populated by the hydrate pipeline when TB ledgers are
  // mapped via custom_class_local_id. Empty when no mappings exist
  // (custom class stays MANUAL_DISCLOSURE — auditor input wins).
  const liveCustomByLocalId = useMemo(
    () => new Map((hydrate.liveCustomClasses ?? []).map(c => [c.localId, c])),
    [hydrate.liveCustomClasses],
  );

  // FSA-2.2 Wave 3 — incoming cross-note refs grouped by target class
  // code. Each class on this Note looks up its bucket; a special "*"
  // bucket holds refs the source pointer didn't pin to a specific class
  // (renders at the top of the Note as "Incoming, unassigned"). Refs are
  // ordered by source schedule name then source line/class for stable
  // visual layout.
  const incomingCrossNoteByTargetClass = useMemo(() => {
    const map = new Map<string, NoteIncomingCrossNote[]>();
    const all = hydrate.incomingCrossNoteAmounts ?? [];
    for (const inc of all) {
      const key = inc.targetClassCode ?? '*';
      const bucket = map.get(key) ?? [];
      bucket.push(inc);
      map.set(key, bucket);
    }
    map.forEach(list =>
      list.sort((a, b) => {
        const s = a.sourceScheduleName.localeCompare(b.sourceScheduleName);
        if (s !== 0) return s;
        const aLabel = a.sourceLineItemName ?? a.sourceClassCode;
        const bLabel = b.sourceLineItemName ?? b.sourceClassCode;
        return aLabel.localeCompare(bLabel);
      }),
    );
    return map;
  }, [hydrate.incomingCrossNoteAmounts]);

  // FSA-2.2 sub-head grouping: parse the schedule's grouping_dimension JSON.
  // Drives whether the Main Note pane renders the flat list (when null)
  // or sub-head groups (Secured / Unsecured for LT Borrowings, etc.).
  // Tolerate bad JSON — fall through to no grouping rather than fail
  // the whole pane render.
  const groupingDimension = useMemo<NoteGroupingDimension | null>(() => {
    if (!hydrate.groupingDimensionJson) return null;
    try {
      const parsed = JSON.parse(hydrate.groupingDimensionJson) as NoteGroupingDimension;
      if (!parsed?.code || !Array.isArray(parsed.options) || parsed.options.length === 0) {
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  }, [hydrate.groupingDimensionJson]);

  // FSA-2.2 Wave B (#3 + #4): template-level cross-note config (label +
  // default target). When set, the line item editor uses `label` as the
  // input field caption and pre-fills the destination schedule + class.
  // NULL = this Note has no cross-note flow; the editor hides the entire
  // cross-note section. Tolerate bad JSON — fall through to null rather
  // than crash the pane.
  const crossNoteConfig = useMemo<NoteCrossNoteConfig | null>(() => {
    if (!hydrate.crossNoteConfigJson) return null;
    try {
      const parsed = JSON.parse(hydrate.crossNoteConfigJson) as NoteCrossNoteConfig;
      if (!parsed?.label || !parsed?.target_schedule_code) return null;
      return parsed;
    } catch {
      return null;
    }
  }, [hydrate.crossNoteConfigJson]);

  // FSA-2.2 sub-head grouping render plan is declared AFTER `renderableClasses`
  // (see below) because it depends on it. Type aliases for that plan live
  // here so they can be referenced anywhere in the component.
  //
  // Wave A — class hierarchy inside groups. A class with line items can
  // appear under MULTIPLE groups (Schedule III convention — Term Loans
  // from Others appears under both Secured and Unsecured with its own
  // line-item subset). Each appearance shows a class header + its line
  // items in that group + a class subtotal scoped to the group. A class
  // without line items renders as a single class-direct row using
  // class.dimension_values for placement.
  type GroupedRenderClassEntry = {
    lc: NoteClassLive;
    cd: NoteClassData;
    /** TRUE when class has no line items — render a single row with
     *  the class amount. FALSE = render class header + line items +
     *  class subtotal in this group. */
    hasOwnRow: boolean;
    /** Line items belonging to this group (filtered). Empty when hasOwnRow. */
    lineItemsInGroup: NoteLineItem[];
    /** Class amount within this group:
     *   - hasOwnRow:        cd.amount_actual / cd.prior_amount_actual
     *   - !hasOwnRow:       Σ filtered line items in this group */
    classSubtotalCy: number;
    classSubtotalPy: number;
    /** Rounded counterparts of the subtotals above. Display layer picks
     *  the actual vs rounded pair based on the modal's display mode so
     *  the Rounded tab shows factor-applied numbers and the Actual tab
     *  shows full-precision rupees. */
    classSubtotalCyRounded: number;
    classSubtotalPyRounded: number;
  };
  type GroupedRenderGroup = {
    optionCode: string | null;     // null = Untagged bucket
    optionLabel: string;
    classes: GroupedRenderClassEntry[];
    subtotalCy: number;
    subtotalPy: number;
    /** Rounded counterparts — see classSubtotalCyRounded above. */
    subtotalCyRounded: number;
    subtotalPyRounded: number;
  };

  // FSA-2.2 saved-as-primary: TRUE when any class (template OR custom)
  // has |saved actual − live actual| > 0.005 across CY or PY. Drives the
  // Note-level "Update from Ledger" toolbar button's enabled state.
  const noteLevelLedgerDrift = useMemo(() => {
    // FSA-2.2 Wave 2 / FSA-2.4 N2 FIX (2026-06-22): per-class gross
    // = saved long-term + class-level outgoing cross-note + line-item
    // outgoing cross-note − line-item INCOMING cross-note / inbound PL.
    //
    // Without subtracting the incoming inflows, this fires on every
    // class that materializes a cross-note or pl_flow line item (e.g.
    // Advances to Suppliers receiving TR sign-sweep — the inflow sits
    // in cd.amount_actual but has no live TB backing, so live = 0
    // and saved = inflow → false-positive drift). Same fix already
    // applied to the per-class drift dot (closingLedgerDrift) and
    // the backend's effective-ledger formula at the Note level.
    //
    // Section-layout notes (Owners'/Partners' Capital) are partner- and
    // movement-driven (Partners master + auditor entry + inbound P&L),
    // NOT TB-leaf-backed — their classes have no mapped ledgers, so the
    // live TB closing is 0 and this saved-vs-ledger check would
    // false-positive a perpetual "snapshot stale" amber. Skip it.
    if (parseDisplayOptions(hydrate.scheduleMetaDataJson)?.sectionLayout === true) {
      return false;
    }
    const grossOf = (cd: NoteClassData | undefined) => {
      if (!cd) return { cy: 0, py: 0, exists: false };
      const liCy = (cd.line_items ?? []).reduce((s, li) => s + (li.cross_note_amount ?? 0), 0);
      const liPy = (cd.line_items ?? []).reduce((s, li) => s + (li.prior_cross_note_amount ?? 0), 0);
      const incomingCy = (cd.line_items ?? [])
        .filter(li => li.allocation_mode === 'cross_note' || li.allocation_mode === 'pl_flow')
        .reduce((s, li) => s + (li.amount_actual ?? 0), 0);
      const incomingPy = (cd.line_items ?? [])
        .filter(li => li.allocation_mode === 'cross_note' || li.allocation_mode === 'pl_flow')
        .reduce((s, li) => s + (li.prior_amount_actual ?? 0), 0);
      return {
        cy: (cd.amount_actual ?? 0) + (cd.cross_note_amount ?? 0) + liCy - incomingCy,
        py: (cd.prior_amount_actual ?? 0) + (cd.prior_cross_note_amount ?? 0) + liPy - incomingPy,
        exists: true,
      };
    };
    for (const c of hydrate.liveClasses) {
      const g = grossOf(workspace.class_data[c.classCode]);
      if (!g.exists) continue;
      if (Math.abs(g.cy - c.liveClosingActual) > 0.005) return true;
      if (Math.abs(g.py - c.liveOpeningActual) > 0.005) return true;
    }
    for (const cc of hydrate.liveCustomClasses ?? []) {
      const g = grossOf(workspace.class_data[cc.localId]);
      if (!g.exists) continue;
      if (Math.abs(g.cy - cc.liveClosingActual) > 0.005) return true;
      if (Math.abs(g.py - cc.liveOpeningActual) > 0.005) return true;
    }
    return false;
  }, [hydrate.liveClasses, hydrate.liveCustomClasses, workspace.class_data, hydrate.scheduleMetaDataJson]);

  // FSA-2.2 Wave 3 (A): Note-level cross-note drift. TRUE when:
  //   • A live aggregator ref exists with no matching saved cross_note
  //     line item (source-added a new one since last save), OR
  //   • A saved cross_note line item's amount differs from the live
  //     aggregator's value for the same source (source amount changed), OR
  //   • A saved cross_note line item has no live counterpart any more
  //     (source removed the ref). Powers the "Update from cross-notes"
  //     button's enabled state.
  const noteLevelCrossNoteDrift = useMemo(() => {
    // Cross-note (cross_note line items) drift detection.
    const live = hydrate.incomingCrossNoteAmounts ?? [];
    const liveByLocalId = new Map(live.map(r => [
      `xn::${r.sourceScheduleCode}::${r.sourceClassCode}::${r.sourceLineItemLocalId ?? '_class'}`,
      r,
    ]));
    const savedXnLocalIds = new Set<string>();
    for (const cd of Object.values(workspace.class_data)) {
      for (const li of cd.line_items ?? []) {
        if (li.allocation_mode !== 'cross_note') continue;
        savedXnLocalIds.add(li.local_id);
        const liveRef = liveByLocalId.get(li.local_id);
        if (!liveRef) return true; // saved exists, live gone
        if (Math.abs((li.amount_actual ?? 0) - (liveRef.amountCy ?? 0)) > 0.005) return true;
        if (Math.abs((li.prior_amount_actual ?? 0) - (liveRef.amountPy ?? 0)) > 0.005) return true;
      }
    }
    for (const [localId] of liveByLocalId) {
      if (!savedXnLocalIds.has(localId)) return true; // live new ref, not yet saved
    }

    // FSA-2.2 Wave 3 — inbound PL flow (pl_flow line items) drift.
    // Same shape: source-added (live but no saved), source removed
    // (saved but no live), or source amount changed.
    const liveFlows = hydrate.inboundPlFlows ?? [];
    const liveFlowByLocalId = new Map(
      liveFlows
        .filter(f => f.distribution === 'SINGLE' && f.sourceIsSaved)
        .map(f => [`pl::${f.sourceReportCode}::${f.sourceLineCode}`, f]),
    );
    const savedPlLocalIds = new Set<string>();
    for (const cd of Object.values(workspace.class_data)) {
      for (const li of cd.line_items ?? []) {
        if (li.allocation_mode !== 'pl_flow') continue;
        savedPlLocalIds.add(li.local_id);
        const liveFlow = liveFlowByLocalId.get(li.local_id);
        if (!liveFlow) return true;
        if (Math.abs((li.amount_actual ?? 0) - (liveFlow.amountCy ?? 0)) > 0.005) return true;
        if (Math.abs((li.prior_amount_actual ?? 0) - (liveFlow.amountPy ?? 0)) > 0.005) return true;
      }
    }
    for (const [localId] of liveFlowByLocalId) {
      if (!savedPlLocalIds.has(localId)) return true;
    }

    return false;
  }, [hydrate.incomingCrossNoteAmounts, hydrate.inboundPlFlows, workspace.class_data]);

  // Synthesise NoteClassLive entries for each added_class so the
  // existing render loop picks them up. Custom classes sit at sibling
  // level (NOT nested) and interleave with template classes by
  // display_order. When TB ledgers ARE mapped via custom_class_local_id
  // we surface the live sums + ledger snapshot (same shape as a leaf
  // class); otherwise the row stays MANUAL_DISCLOSURE: zero TB-side
  // amounts, no ledgers — the auditor's typed amounts come from
  // workspace.class_data[local_id].
  const renderableClasses = useMemo<NoteClassLive[]>(() => {
    const synthetic: NoteClassLive[] = (workspace.added_classes ?? []).map(added => {
      const live = liveCustomByLocalId.get(added.local_id);
      const hasMappings = !!live && live.ledgerCount > 0;
      return {
        classId: added.local_id,
        classCode: added.local_id,
        className: added.name,
        // MANUAL_DISCLOSURE keeps amounts auditor-driven for empty rows;
        // PRIMARY makes the row behave like a leaf class (rounded values
        // are still editable per the audit-independence rule, but
        // amount_actual reflects live TB).
        classRole: hasMappings ? 'PRIMARY' : 'MANUAL_DISCLOSURE',
        displayOrder: added.display_order,
        liveClosingActual: hasMappings ? live!.liveClosingActual : 0,
        liveOpeningActual: hasMappings ? live!.liveOpeningActual : 0,
        ledgerCount: hasMappings ? live!.ledgerCount : 0,
        ledgers: hasMappings ? live!.ledgers : [],
        parentClassId: null,
      };
    });
    return [...hydrate.liveClasses, ...synthetic].sort(
      (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0),
    );
  }, [hydrate.liveClasses, workspace.added_classes, liveCustomByLocalId]);

  // FSA-2.2 sub-head grouping render plan. Builds a flat-by-group view of
  // the Note: each option in the dimension becomes a group; rows under
  // each group are line items + class-without-line-items entries whose
  // dimension_values match the option. Untagged rows fall into a final
  // "Untagged" group so the auditor can fix them. NULL when no grouping
  // is active (the pane renders via the existing flat path instead).
  const groupedRenderPlan = useMemo<GroupedRenderGroup[] | null>(() => {
    if (!groupingDimension) return null;
    const dimCode = groupingDimension.code;

    // Initialise one bucket per declared option, in declared order, then
    // an "Untagged" trailing bucket. Empty buckets get dropped at the end.
    const buckets = new Map<string | null, GroupedRenderGroup>();
    for (const opt of groupingDimension.options) {
      buckets.set(opt.code, {
        optionCode: opt.code, optionLabel: opt.label,
        classes: [],
        subtotalCy: 0, subtotalPy: 0,
        subtotalCyRounded: 0, subtotalPyRounded: 0,
      });
    }
    buckets.set(null, {
      optionCode: null, optionLabel: 'Untagged',
      classes: [],
      subtotalCy: 0, subtotalPy: 0,
      subtotalCyRounded: 0, subtotalPyRounded: 0,
    });

    for (const lc of renderableClasses) {
      const cd = workspace.class_data[lc.classCode];
      if (!cd) continue;
      const lineItems = cd.line_items ?? [];

      if (lineItems.length > 0) {
        // Class hierarchy under groups: walk line items, bucket them
        // per their dimension_value. For each bucket that received any
        // line item, the CLASS gets a class entry IN THAT BUCKET. A
        // class can therefore appear under multiple groups, each
        // showing only its line items belonging to that group plus a
        // group-scoped class subtotal.
        // Bucketing inheritance: a line item without its own
        // dimension_values[dimCode] falls back to the parent class's
        // dimension_values[dimCode]. Lets the auditor (or the template
        // default) tag the class once and have every child line item
        // pick up the same group automatically. Source-owned lines
        // (cross_note / pl_flow) materialise without per-line dim
        // values, so without this fallback they default to Untagged
        // even when their host class is unambiguously tagged.
        const classFallbackDim = cd.dimension_values?.[dimCode] ?? null;
        const linesByBucket = new Map<string | null, NoteLineItem[]>();
        for (const li of lineItems) {
          const liOwnVal = li.dimension_values?.[dimCode] ?? null;
          const effective = liOwnVal ?? classFallbackDim;
          const bucketKey = (effective != null && buckets.has(effective)) ? effective : null;
          const list = linesByBucket.get(bucketKey) ?? [];
          list.push(li);
          linesByBucket.set(bucketKey, list);
        }
        for (const [bucketKey, lineItemsInBucket] of linesByBucket) {
          const bucket = buckets.get(bucketKey) ?? buckets.get(null)!;
          const cyTotal = lineItemsInBucket.reduce((s, li) => s + (li.amount_actual ?? 0), 0);
          const pyTotal = lineItemsInBucket.reduce((s, li) => s + (li.prior_amount_actual ?? 0), 0);
          const cyTotalR = lineItemsInBucket.reduce((s, li) => s + (li.amount_rounded ?? 0), 0);
          const pyTotalR = lineItemsInBucket.reduce((s, li) => s + (li.prior_amount_rounded ?? 0), 0);
          bucket.classes.push({
            lc,
            cd,
            hasOwnRow: false,
            lineItemsInGroup: lineItemsInBucket,
            classSubtotalCy: cyTotal,
            classSubtotalPy: pyTotal,
            classSubtotalCyRounded: cyTotalR,
            classSubtotalPyRounded: pyTotalR,
          });
          bucket.subtotalCy += cyTotal;
          bucket.subtotalPy += pyTotal;
          bucket.subtotalCyRounded += cyTotalR;
          bucket.subtotalPyRounded += pyTotalR;
        }
      } else {
        // No line items — the class itself is a row. Bucketed via the
        // class-level dimension_values. Falls into Untagged when missing.
        const classVal = cd.dimension_values?.[dimCode] ?? null;
        const bucketKey = (classVal != null && buckets.has(classVal)) ? classVal : null;
        const bucket = buckets.get(bucketKey) ?? buckets.get(null)!;
        bucket.classes.push({
          lc,
          cd,
          hasOwnRow: true,
          lineItemsInGroup: [],
          classSubtotalCy: cd.amount_actual ?? 0,
          classSubtotalPy: cd.prior_amount_actual ?? 0,
          classSubtotalCyRounded: cd.amount_rounded ?? 0,
          classSubtotalPyRounded: cd.prior_amount_rounded ?? 0,
        });
        bucket.subtotalCy += cd.amount_actual ?? 0;
        bucket.subtotalPy += cd.prior_amount_actual ?? 0;
        bucket.subtotalCyRounded += cd.amount_rounded ?? 0;
        bucket.subtotalPyRounded += cd.prior_amount_rounded ?? 0;
      }
    }

    // Keep dimension-defined buckets in their declared order; drop the
    // Untagged bucket when empty.
    return Array.from(buckets.values()).filter(b => b.classes.length > 0 || b.optionCode !== null);
  }, [groupingDimension, renderableClasses, workspace.class_data]);

  /**
   * Compute the decimal display_order to insert a new custom class at
   * the chosen anchor position. Algorithm:
   *   - "START": value is less than the first sibling's order. We use
   *     firstOrder - 1 (or 0 when there are no siblings).
   *   - "END" / unknown anchor: maxOrder + 1.
   *   - "after-<id>": midpoint between that sibling and the next one
   *     in the sorted top-level list. If no next sibling, anchorOrder + 1.
   * The merged renderableClasses already exposes the sorted siblings,
   * so we walk them once.
   */
  const computeAnchorOrder = useCallback((anchor: string): number => {
    const siblings = renderableClasses;
    if (siblings.length === 0) return 0;
    if (anchor === 'START') return (siblings[0].displayOrder ?? 0) - 1;
    if (anchor === 'END') {
      const maxOrder = siblings.reduce((m, s) => Math.max(m, s.displayOrder ?? 0), 0);
      return maxOrder + 1;
    }
    // After-<id>: id is classId-as-string for templates, local_id for customs.
    if (anchor.startsWith('AFTER:')) {
      const id = anchor.slice('AFTER:'.length);
      const idx = siblings.findIndex(s => String(s.classId) === id);
      if (idx === -1) {
        // anchor disappeared (e.g. template structure changed) — fall back to END.
        const maxOrder = siblings.reduce((m, s) => Math.max(m, s.displayOrder ?? 0), 0);
        return maxOrder + 1;
      }
      const anchorOrder = siblings[idx].displayOrder ?? 0;
      const nextOrder = idx + 1 < siblings.length
        ? (siblings[idx + 1].displayOrder ?? anchorOrder + 1)
        : null;
      return nextOrder != null
        ? (anchorOrder + nextOrder) / 2
        : anchorOrder + 1;
    }
    return 0;
  }, [renderableClasses]);

  const handleConfirmCustomClass = useCallback(() => {
    if (!customClassDraft) return;
    const name = customClassDraft.name.trim();
    if (!name) return;
    if (customClassDraft.mode === 'add') {
      const displayOrder = computeAnchorOrder(customClassDraft.anchor);
      addCustomClass(name, displayOrder);
    } else if (customClassDraft.mode === 'rename') {
      renameCustomClass(customClassDraft.localId, name);
    } else {
      // rename-template: save per-engagement label override.
      setClassLabelOverride(customClassDraft.classCode, name);
    }
    setCustomClassDraft(null);
    // Framing 1 auto-save — Add and Rename are both structural commits
    // the auditor explicitly triggered via the editor's Save button.
    requestAutoSave?.();
  }, [customClassDraft, addCustomClass, renameCustomClass, setClassLabelOverride, computeAnchorOrder, requestAutoSave]);

  // Inline editor state, scoped by classCode. At most one class is in
  // "editing" state at a time — clicking + Add Line on a different class
  // replaces it.
  const [editing, setEditing] = useState<EditingState | null>(null);

  const handleAddLine = useCallback((classCode: string) => {
    // Inherit the parent class's dimension_values (e.g. security
    // type — Secured / Unsecured). The auditor can override per line
    // item; defaulting to the class's tag saves them a click in the
    // common case where every line item sits under the same dimension.
    const parentCd = workspace.class_data[classCode];
    const inheritedDimensionValues = { ...(parentCd?.dimension_values ?? {}) };
    setEditing({
      classCode,
      mode: 'add',
      draft: { ...blankDraft(), dimension_values: inheritedDimensionValues },
    });
  }, [workspace.class_data]);

  const handleEditLine = useCallback((classCode: string, li: NoteLineItem) => {
    // FSA-2.2 — class-level cross-note materialized lines (sign-sweep
    // targets) get a rename-only editor. They're cross_note mode with
    // no source line item id (source is class-level upstream).
    const renameOnly =
      li.allocation_mode === 'cross_note' && !li.source_ref?.line_item_local_id;
    // FSA-2.2 multi-ledger line items: pre-populate EVERY selected ledger
    // id so the multi-select editor shows the auditor's saved choices on
    // open. Previously only li.ledgers[0] was carried in — anything past
    // the first ledger silently disappeared from the edit form.
    setEditing({
      classCode,
      mode: 'edit',
      localId: li.local_id,
      renameOnly,
      draft: {
        name: li.name,
        allocation_mode: li.allocation_mode,
        ledger_ids:
          li.allocation_mode === 'ledgers'
            ? li.ledgers
                .filter(l => l.ledger_id != null)
                .map(l => String(l.ledger_id))
            : [],
        manual_amount_actual: li.manual_amount_actual ?? li.amount_actual ?? 0,
        manual_prior_amount_actual: li.prior_amount_actual ?? 0,
        // FSA-2.2 sub-head grouping: carry over saved dimension tags so
        // the editor shows the auditor's prior choice.
        dimension_values: { ...(li.dimension_values ?? {}) },
        // FSA-2.2 cross-note amount: carry the auditor's prior entry +
        // destination pointer so the edit form shows them.
        cross_note_amount: li.cross_note_amount ?? 0,
        prior_cross_note_amount: li.prior_cross_note_amount ?? 0,
        cross_note_target: li.cross_note_target ?? null,
      },
    });
  }, []);

  const handleDeleteLine = useCallback(
    (classCode: string, localId: string) => {
      const cd = workspace.class_data[classCode];
      if (!cd) return;
      setLineItems(classCode, cd.line_items.filter(li => li.local_id !== localId));
    },
    [workspace.class_data, setLineItems],
  );

  const handleSaveLine = useCallback(
    (classCode: string, ledgers: NoteHydrate['liveClasses'][number]['ledgers'], draft: LineItemDraft) => {
      const cd = workspace.class_data[classCode];
      if (!cd) return;
      const name = draft.name.trim();
      if (!name) return;

      const isEditMode = editing?.mode === 'edit' && editing.classCode === classCode;
      const targetLocalId = isEditMode ? editing!.localId! : crypto.randomUUID();

      const factor = hydrate.roundingFactorKey;
      const next: NoteLineItem | null = (() => {
        if (draft.allocation_mode === 'ledgers') {
          // FSA-2.2 multi-ledger line items: map every selected id to its
          // live ledger row and sum the closing/opening amounts into the
          // line item total. Empty selection ⇒ invalid (caught upstream).
          const liveById = new Map(ledgers.map(l => [String(l.ledgerId), l]));
          const picked = draft.ledger_ids
            .map(id => liveById.get(id))
            .filter((l): l is typeof ledgers[number] => !!l);
          if (picked.length === 0) return null;
          const closingSum = picked.reduce((s, l) => s + l.closingActual, 0);
          const openingSum = picked.reduce((s, l) => s + l.openingActual, 0);
          // FSA-2.2 Wave 2 cross-note: save-time, the line item's
          // amount_actual = LONG-TERM (gross live sum minus the
          // auditor-entered cross_note_amount). On hydrate this is
          // what's displayed; stale detection adds cross_note_amount
          // back to compare against live TB. Use 0 when no cross-note.
          const crossNoteCy = draft.cross_note_amount ?? 0;
          const crossNotePy = draft.prior_cross_note_amount ?? 0;
          const longTermCy = closingSum - crossNoteCy;
          const longTermPy = openingSum - crossNotePy;
          const cyLongTermRoundAuto = autoRound(longTermCy, factor);
          const pyLongTermRoundAuto = autoRound(longTermPy, factor);
          return {
            local_id: targetLocalId,
            name,
            display_order: 0,
            allocation_mode: 'ledgers',
            ledgers: picked.map((l, i) => ({
              ledger_id: l.ledgerId,
              ledger_name: l.ledgerName,
              amount_actual: l.closingActual,
              prior_amount_actual: l.openingActual,
              display_order: i + 1,
              remarks: null,
            })),
            manual_amount_actual: null,
            amount_actual: longTermCy,
            amount_rounded_auto: cyLongTermRoundAuto,
            amount_rounded: cyLongTermRoundAuto,
            prior_amount_actual: longTermPy,
            prior_amount_rounded_auto: pyLongTermRoundAuto,
            prior_amount_rounded: pyLongTermRoundAuto,
            remarks: null,
            dimension_values: { ...draft.dimension_values },
            // Use !== 0 (not > 0): credit-balance schedules store the
            // auditor's entry as a negative number after the sign flip.
            // > 0 silently dropped it to null and the field came back
            // blank on re-edit.
            // The TARGET is kept whenever EITHER period is routed — a
            // CY-only gate dropped the pointer on a nil-current-year flow
            // (Long-term → Short-term Borrowings with a comparative-only
            // current maturity), so the destination Note never saw it.
            cross_note_amount: crossNoteCy !== 0 ? crossNoteCy : null,
            prior_cross_note_amount: crossNotePy !== 0 ? crossNotePy : null,
            cross_note_target: (crossNoteCy !== 0 || crossNotePy !== 0) ? draft.cross_note_target : null,
          };
        }
        // FSA-2.2 Wave 2 cross-note (manual mode): the auditor's manual
        // amount IS the long-term value. Cross-note is its own field;
        // gross-equivalent for drift = amount_actual + cross_note_amount.
        const crossNoteCy = draft.cross_note_amount ?? 0;
        const crossNotePy = draft.prior_cross_note_amount ?? 0;
        const cyRoundAuto = autoRound(draft.manual_amount_actual, factor);
        const pyRoundAuto = autoRound(draft.manual_prior_amount_actual, factor);
        return {
          local_id: targetLocalId,
          name,
          display_order: 0,
          allocation_mode: 'manual',
          ledgers: [],
          manual_amount_actual: draft.manual_amount_actual,
          amount_actual: draft.manual_amount_actual,
          amount_rounded_auto: cyRoundAuto,
          amount_rounded: cyRoundAuto,
          prior_amount_actual: draft.manual_prior_amount_actual,
          prior_amount_rounded_auto: pyRoundAuto,
          prior_amount_rounded: pyRoundAuto,
          remarks: null,
          dimension_values: { ...draft.dimension_values },
          // See ledger-mode comment above — !== 0 keeps the credit-balance
          // negative entries from being silently dropped to null, and the
          // target survives on a PY-only (nil current year) flow.
          cross_note_amount: crossNoteCy !== 0 ? crossNoteCy : null,
          prior_cross_note_amount: crossNotePy !== 0 ? crossNotePy : null,
          cross_note_target: (crossNoteCy !== 0 || crossNotePy !== 0) ? draft.cross_note_target : null,
        };
      })();
      if (!next) return;

      let updated: NoteLineItem[];
      if (isEditMode) {
        updated = cd.line_items.map(li => (li.local_id === targetLocalId ? next : li));
      } else {
        updated = [...cd.line_items, next];
      }
      updated = updated.map((li, i) => ({ ...li, display_order: (i + 1) * 10 }));
      setLineItems(classCode, updated);
      setEditing(null);
      // Framing 1 auto-save — the editor's Save button is an explicit
      // commit affordance, so both Add and Edit confirm flows persist
      // the workspace immediately. Auditor's typed amounts are saved
      // and stay editable for later corrections.
      requestAutoSave?.();
    },
    [workspace.class_data, setLineItems, editing, hydrate.roundingFactorKey, requestAutoSave],
  );

  // Inline reorder for line items. The auditor owns presentation order
  // even for source-owned rows (cross_note / pl_flow) — amount stays
  // locked but position is auditor-controlled. Swap with the previous
  // or next sibling in the class's line_items array, then renumber
  // display_order so the saved order matches the rendered order.
  //
  // Intentionally does NOT call requestAutoSave — reorder lives in
  // workspace state until the auditor commits via the Note's Save
  // button. Lets them stage a series of moves (Up Up Down Up) without
  // a save-per-click flicker; isDirty flips on the first swap and the
  // Save button enables; one final Save persists every change.
  const handleMoveLine = useCallback(
    (classCode: string, localId: string, direction: 'up' | 'down') => {
      const cd = workspace.class_data[classCode];
      if (!cd) return;
      const items = cd.line_items ?? [];
      const idx = items.findIndex(li => li.local_id === localId);
      if (idx < 0) return;
      const target = direction === 'up' ? idx - 1 : idx + 1;
      if (target < 0 || target >= items.length) return;
      const next = items.slice();
      const tmp = next[idx];
      next[idx] = next[target];
      next[target] = tmp;
      const renumbered = next.map((li, i) => ({ ...li, display_order: (i + 1) * 10 }));
      setLineItems(classCode, renumbered);
    },
    [workspace.class_data, setLineItems],
  );

  const tolerance = hydrate.roundingTolerance ?? 1.0;
  // FSA-2.4 — factorLabel kept locally for any remaining internal tooltips
  // / titles that still reference the engagement rounding factor. The
  // Actual/Rounded toggle itself moved to the modal header.
  const factorLabel = hydrate.roundingFactorKey ?? 'ACTUALS';
  const invertSign = !!hydrate.isCreditBalance;

  // Schedule-level display flags (parsed once from meta_data JSON).
  // Defaults: show_total = true, no class is rounding-exempt.
  const scheduleDisplayOptions = useMemo(
    () => parseDisplayOptions(hydrate.scheduleMetaDataJson),
    [hydrate.scheduleMetaDataJson],
  );
  const showNoteTotal = scheduleDisplayOptions?.showTotal !== false;
  const isSectionLayout = scheduleDisplayOptions?.sectionLayout === true;

  // Section-grid Notes render one collapsible band (single-class grid) per
  // top-level class; each band instance therefore can't own the note-wide
  // column_totals (Σ across ALL classes). Compute it centrally here so the
  // note-wide column_sum value_sources (e.g. P&L Partners' Remuneration =
  // Σ remuneration across both sections) keep resolving. Sums every class's
  // persisted column_values across the amount columns + their __rounded
  // companions, then writes once via the shared setColumnTotals.
  // Uses the one shared derivation (notes/shared/columnTotals) so this, the
  // MultiColumnClassTable footer and the save-time normaliser can't disagree
  // — three divergent copies of this sum are what left PPE permanently dirty.
  useEffect(() => {
    if (!isSectionLayout) return;
    setColumnTotals(sumColumnTotals(workspace.class_data, multiColumnSpec?.columns ?? []));
  }, [isSectionLayout, workspace.class_data, multiColumnSpec, setColumnTotals]);
  // Per-class rounding-exempt map. Classes flagged exempt skip the
  // engagement rounding factor in the Rounded view — used on Note 35
  // EPS for share counts + per-share Rs values where the factor's
  // unit conversion is meaningless.
  const roundingExemptByCode = useMemo(() => {
    const map = new Map<string, boolean>();
    (hydrate.liveClasses ?? []).forEach(lc => {
      const opts = parseDisplayOptions(lc.metaDataJson);
      if (opts?.roundingExempt === true) map.set(lc.classCode, true);
    });
    return map;
  }, [hydrate.liveClasses]);
  // Pure display helper: flip sign for credit schedules so the auditor
  // sees natural positive numbers. Stored values stay signed.
  const displaySigned = (n: number): number => invertSign ? -n : n;

  // ───── totals ──────────────────────────────────────────────────────────────
  // Note total = Σ cd.amount_actual via Option A rollup. cross_note-mode
  // line items already sit inside cd.line_items (materialised by the
  // hydrate merge in FsaNoteModal), so cd.amount_actual reflects own +
  // cross_note contributions naturally.
  //
  // MEMO_ONLY classes (e.g. Authorised Capital on Note 1) render with
  // their own per-class total but are EXCLUDED from the Note Total
  // footer — they're disclosure-only blocks. This mirrors the backend
  // save service's totalling filter so the displayed footer matches
  // what gets persisted as closing_actual_total / closing_rounded_total.
  const classRoleByCode = new Map<string, string | null | undefined>(
    hydrate.liveClasses.map(lc => [lc.classCode, lc.classRole]),
  );
  const isMemoOnly = (code: string): boolean => {
    const role = classRoleByCode.get(code);
    return typeof role === 'string' && role.toUpperCase() === 'MEMO_ONLY';
  };
  let cActual = 0, cRounded = 0, oActual = 0, oRounded = 0;
  for (const [code, cd] of Object.entries(workspace.class_data)) {
    if (isMemoOnly(code)) continue;
    cActual += cd.amount_actual;
    cRounded += cd.amount_rounded;
    oActual += cd.prior_amount_actual;
    oRounded += cd.prior_amount_rounded;
  }

  // ───── schedule-level reconciliation ───────────────────────────────────────
  // TB Mapped (live sum across all entries on this schedule, class-assigned
  // and schedule-only alike) vs Note Total (sum of class_data amounts in
  // the workspace). Drift > 0 → there's TB content that isn't reflected in
  // any class total OR manual class amounts under-/over-allocate the pool.
  // Future FSA-3 adjustment journals will be added into Mapped.
  //
  // Defensive `Number.isFinite` guards: if the wire shape is unexpected
  // (e.g. backend not rebuilt after the ScheduleOnlyPool → MappedTotal
  // rename), treat missing values as 0 instead of letting NaN propagate.
  const safeMappedCy = Number.isFinite(hydrate.mappedTotalCy) ? hydrate.mappedTotalCy : 0;
  const safeMappedPy = Number.isFinite(hydrate.mappedTotalPy) ? hydrate.mappedTotalPy : 0;
  const reconNoteTotalCy = cActual;
  const reconNoteTotalPy = oActual;
  const reconDriftBannerCy = +(safeMappedCy - reconNoteTotalCy).toFixed(2);
  const reconDriftBannerPy = +(safeMappedPy - reconNoteTotalPy).toFixed(2);
  // Pure floating-point safety threshold — actuals should match exactly by
  // construction. Anything beyond rounding noise is a real drift.
  const reconMatchesCy = Math.abs(reconDriftBannerCy) <= 0.01;
  const reconMatchesPy = Math.abs(reconDriftBannerPy) <= 0.01;
  const reconAllMatch = reconMatchesCy && reconMatchesPy;

  // FSA-2.4 N3 — Deviation items consolidator (drift evaluator v2).
  // Reads backend-authoritative noteLevelDrift + the component
  // breakdown to surface drift items that account for cross-note
  // flows + sign-sweep + PL→Reserves. The panel is the single source
  // of "what's wrong on this Note" — sidebar Drift group, Submit
  // guard, and exception-strip rollups all read consistent numbers.
  const fmtInr = (v: number) =>
    Math.abs(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  // FSA ICAI NCE — partner-note context. `partnerMasterPsr` feeds the
  // "differs from Partners master" chip on partner rows in the section grid
  // (display + compute use the workspace's Share %; the chip only signals
  // the master default has diverged). `hasPartnerLinesClass` gates the
  // partner-note precondition hints in the deviation panel below.
  const hasPartnerLinesClass = useMemo(() => (hydrate.liveClasses ?? []).some(c => {
    try { return (JSON.parse(c.metaDataJson ?? '{}') as { partner_lines?: boolean })?.partner_lines === true; }
    catch { return false; }
  }), [hydrate.liveClasses]);
  const partnerMasterPsr = useMemo(() => {
    const m: Record<string, number> = {};
    for (const p of hydrate.partners ?? []) m[`partner::${p.id}`] = p.profitSharingRatio ?? 0;
    return m;
  }, [hydrate.partners]);

  const deviationItems = useMemo<NoteDeviationItem[]>(() => {
    const out: NoteDeviationItem[] = [];
    const driftCy = hydrate.noteLevelDriftCy ?? 0;
    const driftPy = hydrate.noteLevelDriftPy ?? 0;

    // Note-level drift (authoritative). 0.005 = half-paisa floating
    // point safety threshold matching the backend.
    if (driftCy > 0.005) {
      out.push({
        id: 'drift-cy',
        severity: 'error',
        label: 'Note total does not match effective ledger (CY)',
        detail: `Drift ₹${fmtInr(driftCy)}`,
      });
    }
    if (driftPy > 0.005) {
      out.push({
        id: 'drift-py',
        severity: 'warning',
        label: 'Note total does not match effective ledger (PY)',
        detail: `Drift ₹${fmtInr(driftPy)}`,
      });
    }

    // FSA-2.4 N2 — Live drift messaging. Cells display LIVE values by
    // default (amber-tinted when ≠ saved); Save auto-applies them so
    // a single click is enough to persist what the auditor sees.
    if (noteLevelLedgerDrift) {
      out.push({
        id: 'pending-ledger',
        severity: 'warning',
        label: 'Saved snapshot is stale — cells highlighted amber show live values',
        detail: 'Click Save to persist the live snapshot',
      });
    }
    if (noteLevelCrossNoteDrift) {
      out.push({
        id: 'pending-cross-note',
        severity: 'warning',
        label: 'Cross-note or PL flow source has changed',
        detail: 'Use "Update from sources" to sync',
      });
    }

    // Informational breakdown — the components of the effective-ledger
    // formula. Helps auditors understand WHY the effective and raw
    // ledger differ for this Note (visible even when reconciled, so
    // the audit story for cross-note routing is always traceable).
    const outgoing = hydrate.outgoingCrossNoteCy ?? 0;
    if (Math.abs(outgoing) > 0.005) {
      out.push({
        id: 'outgoing-cross-note',
        severity: 'info',
        label: `₹${fmtInr(outgoing)} routed out to other notes`,
        detail: 'Subtracted from raw ledger to compute effective ledger',
      });
    }
    const incoming = hydrate.incomingCrossNoteTotalCy ?? 0;
    if (Math.abs(incoming) > 0.005) {
      out.push({
        id: 'incoming-cross-note',
        severity: 'info',
        label: `₹${fmtInr(incoming)} received from other notes`,
        detail: 'Added to raw ledger to compute effective ledger',
      });
    }
    const inboundPl = hydrate.inboundPlTotalCy ?? 0;
    if (Math.abs(inboundPl) > 0.005) {
      out.push({
        id: 'inbound-pl',
        severity: 'info',
        label: `₹${fmtInr(inboundPl)} inbound from P&L closing`,
        detail: 'Added to raw ledger to compute effective ledger',
      });
    }

    // FSA-2.4 N3 — Audit adjustments folded into mapped total. Surfaces
    // so the auditor sees how much of the effective ledger comes from
    // posted JEs vs raw TB. Mapped total = consolidated TB + JE net;
    // both compose the audited figure used in the formula.
    const journalNet = hydrate.journalNetCy ?? 0;
    if (Math.abs(journalNet) > 0.005) {
      out.push({
        id: 'journal-net',
        severity: 'info',
        label: `₹${fmtInr(journalNet)} audit adjustments included in ledger`,
        detail: 'Posted JE net folded into mapped total',
      });
    }

    // NCP-10 — Partners' profit-share allocation drift (Owners' Capital).
    // The P&L Profit/(Loss) routed in via inbound_pl_flow (distribution
    // AUDITOR) must be fully allocated across the partner rows of the section
    // the profit share is ROUTED to (Σ share_of_profit_loss). The destination
    // is per-engagement configurable (Capital vs Current Account), so read the
    // effective appropriation_routing rather than assuming Current. This
    // compares ONLY the profit-share column against the P&L profit —
    // remuneration / interest-on-capital are separate P&L adjustments and
    // deliberately excluded. A per-partner rounding tolerance (±0.5 each,
    // floor 1.0) absorbs the residual from splitting an odd profit across
    // fractional PSRs so the auditor isn't false-flagged on a rounding gap.
    const partnerFlow = (hydrate.inboundPlFlows ?? []).find(f => f.distribution === 'AUDITOR');
    if (partnerFlow?.sourceIsSaved) {
      let profitSection = partnerFlow.targetClassCode ?? 'PC_CURRENT_ACCOUNT';
      try {
        const md = JSON.parse(hydrate.scheduleMetaDataJson ?? '{}') as { appropriation_routing?: Record<string, string> };
        const eff = { ...(md?.appropriation_routing ?? {}), ...(workspace.appropriation_routing ?? {}) };
        profitSection = eff['share_of_profit_loss'] ?? profitSection;
      } catch { /* keep default */ }
      const cd = workspace.class_data?.[profitSection];
      const rows = cd?.line_items ?? [];
      if (rows.length > 0) {
        const target = partnerFlow.amountCy ?? 0;
        const allocated = rows.reduce(
          (s, li) => s + (li.column_values?.share_of_profit_loss ?? 0), 0,
        );
        const unallocated = target - allocated;
        const tol = Math.max(1.0, rows.length * 0.5);
        if (Math.abs(unallocated) > tol) {
          out.push({
            id: 'partner-profit-share-drift',
            severity: 'warning',
            label: 'Partners’ profit share does not add up to the P&L profit',
            detail: `Allocated ₹${fmtInr(allocated)} of ₹${fmtInr(target)} — unallocated ₹${fmtInr(unallocated)}`,
          });
        }
      }
    }

    // NCP — partner-note preconditions, surfaced instead of silent zeros.
    // (a) No eligible partners: the Partners-master query filters by the
    //     effective window containing the period end — an empty roster means
    //     no rows materialise and no profit distributes, with no other
    //     visible symptom. (b) Profit source unsaved: the AUDITOR-distribution
    //     inbound P&L flow only carries an amount once the source Statement
    //     is saved; until then Share of Profit/(Loss) stays 0 by design.
    if (hasPartnerLinesClass && (hydrate.partners ?? []).length === 0) {
      out.push({
        id: 'partners-none-eligible',
        severity: 'warning',
        label: 'No eligible partners found for this engagement',
        detail: 'Add partners under FSA → Partners and check their Effective From / To dates cover the period end — partner rows, Share % and profit distribution cannot auto-fill without them',
      });
    }
    if (hasPartnerLinesClass && partnerFlow && !partnerFlow.sourceIsSaved) {
      out.push({
        id: 'partner-profit-source-unsaved',
        severity: 'info',
        label: 'Profit not yet distributed to partners',
        detail: 'Save the Statement of Profit & Loss — the Share of Profit/(Loss) column fills from its saved profit × each partner’s Share %',
      });
    }

    // Hidden-in-print classes that CARRY amounts. The PDF drops the whole
    // class/section, so a class hidden while it was nil (e.g. "Hide empty"
    // on the Owners' Capital Current Account before the profit share was
    // routed into it) silently disappears from the printed note along with
    // the amounts it later received. Promised by setClassHideInPrint's
    // design note; the auditor unhides via the class menu → Show in print.
    const amountColCodes = new Set(
      (multiColumnSpec?.columns ?? [])
        .filter(c => c.data_type === 'amount')
        .map(c => c.code),
    );
    const hiddenWithAmounts: string[] = [];
    for (const [code, cd] of Object.entries(workspace.class_data ?? {})) {
      if (!cd?.hide_in_print) continue;
      const legacyNonZero = Math.abs(cd.amount_actual ?? 0) > 0.005
        || Math.abs(cd.prior_amount_actual ?? 0) > 0.005;
      const cvNonZero = Object.entries(cd.column_values ?? {}).some(([k, v]) =>
        amountColCodes.has(k) && typeof v === 'number' && Math.abs(v) > 0.005);
      if (!legacyNonZero && !cvNonZero) continue;
      const name = (hydrate.liveClasses ?? []).find(c => c.classCode === code)?.className
        ?? (workspace.added_classes ?? []).find(a => a.local_id === code)?.name
        ?? code;
      hiddenWithAmounts.push(name);
    }
    if (hiddenWithAmounts.length > 0) {
      out.push({
        id: 'hidden-class-with-amounts',
        severity: 'warning',
        label: `Hidden in print but carrying amounts: ${hiddenWithAmounts.join(', ')}`,
        detail: 'These classes are skipped in the printed note and preview — class menu → Show in print restores them',
      });
    }

    return out;
  }, [
    multiColumnSpec,
    hydrate.liveClasses,
    workspace.added_classes,
    hasPartnerLinesClass,
    hydrate.partners,
    hydrate.noteLevelDriftCy,
    hydrate.noteLevelDriftPy,
    hydrate.outgoingCrossNoteCy,
    hydrate.incomingCrossNoteTotalCy,
    hydrate.inboundPlTotalCy,
    hydrate.inboundPlFlows,
    hydrate.journalNetCy,
    hydrate.scheduleMetaDataJson,
    workspace.class_data,
    workspace.appropriation_routing,
    noteLevelLedgerDrift,
    noteLevelCrossNoteDrift,
  ]);

  return (
    <div style={paneStyle}>
      <div style={toolbarStyle}>
        {/* FSA-2.4 — Display: Actual/Rounded toggle moved to the modal
            header so it controls the whole Note (Main + Additional
            Disclosures) from one place. Toolbar now hosts display-
            adjacent actions (Hide empty, Reset rounding) on the left
            and data actions on the right of the flex spacer. */}
        {!readOnly && (
          <>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={autoHideEmptyClasses}
              title="Mark every class whose CY+PY amounts are all zero as hidden from the printed PDF. Idempotent — re-running won't change anything once flagged. Reverse per-class via the row action menu."
            >
              <IconSystem.eyeOff size={13} /> Hide empty
            </button>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={resetRoundingFromActuals}
              title="Replace user-edited rounded values with fresh auto-rounded values"
            >
              <IconSystem.refresh size={13} /> Reset rounding
            </button>
          </>
        )}
        <div style={{ flex: 1 }} />
        {!readOnly && (
          <>
            {/*
             * Header-level "+ Add" dropdown. Two options per the
             * disclosure flexibility design:
             *   - Add Custom Class → placeholder toast (ships in
             *     rollout step 3 — Notes-side custom classification).
             *   - Add Note Text   → reveals the deliverable note_remark
             *     editor below the totals.
             */}
            <ActionMenu
              triggerContent={
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <IconSystem.add size={13} /> Add
                </span>
              }
              triggerClassName="btn btn-secondary btn-sm"
              items={[
                {
                  id: 'add-custom-class',
                  label: 'Add Custom Class',
                  icon: <IconSystem.add size={14} />,
                  onClick: handleAddCustomClass,
                },
                {
                  id: 'add-note-text',
                  label: noteTextVisible ? 'Note Text (visible)' : 'Add Note Text',
                  icon: <IconSystem.file size={14} />,
                  onClick: handleRevealNoteText,
                  disabled: noteTextVisible,
                },
              ]}
              position="bottom-right"
            />
            {/* FSA-2.2 saved-as-primary: Note-level "Update all from
                Ledger" — pulls the current live snapshot into every
                class_data entry in one shot. Disabled when no drift
                exists across any class so the auditor isn't tempted to
                re-pull identical values (which would still dirty the
                workspace and force a no-op save). */}
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={applyAllLedgerSnapshots}
              disabled={!noteLevelLedgerDrift}
              title={
                noteLevelLedgerDrift
                  ? 'Apply live ledger snapshot to the workspace without saving (so you can review before persisting). Save now does this automatically — this button is for the case where you want to inspect the applied state first.'
                  : 'No drift between saved values and the live trial balance — nothing to update.'
              }
            >
              <IconSystem.refresh size={13} /> Update from Ledger
            </button>
            {/* "Recalculate Totals" — line_items → class totals across
                every class (incl. engagement-time custom classes). Doesn't
                touch ledgers or line items; only re-derives class-level
                amount_actual / amount_rounded / *_auto. Fixes a stale
                saved total when the auditor has edited a line item's
                rounded value inline, or when a custom-class total drifted
                in a pre-fix save. Save activates afterwards. */}
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={recalculateAllTotals}
              title="Re-derive every class's totals from the current line-item sums (CY + PY, actual + rounded). Doesn't touch ledgers or line items themselves — just rebuilds the class-level totals so the Note total reflects the latest line-item values."
            >
              <IconSystem.refresh size={13} /> Recalculate Totals
            </button>
            {(() => {
              // Show the bulk refresh button when ANY of these are true:
              //   • The live aggregator reports incoming cross-note refs
              //     targeting this Note.
              //   • The Note has saved cross_note-mode line items
              //     (potential orphans if the source removed the ref).
              //   • The template declares an inbound_pl_flow on this
              //     schedule, OR the workspace already carries pl_flow
              //     line items.
              const hasLiveIncoming = (hydrate.incomingCrossNoteAmounts?.length ?? 0) > 0;
              const hasLiveInboundPl = (hydrate.inboundPlFlows?.length ?? 0) > 0;
              const hasSavedManagedLines = Object.values(workspace.class_data).some(
                cd => (cd.line_items ?? []).some(li =>
                  li.allocation_mode === 'cross_note' || li.allocation_mode === 'pl_flow',
                ),
              );
              if (!hasLiveIncoming && !hasLiveInboundPl && !hasSavedManagedLines) return null;
              return (
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={applyAllCrossNoteSnapshots}
                disabled={!noteLevelCrossNoteDrift}
                title={
                  noteLevelCrossNoteDrift
                    ? 'Refresh every cross_note + pl_flow line item from its source (other Notes, saved Statements). New refs are added, changed amounts updated, dropped sources removed. Save the Note afterwards to persist.'
                    : 'Managed line items already match every active source — nothing to update.'
                }
              >
                <IconSystem.refresh size={13} /> Update from sources
              </button>
              );
            })()}
            {/* FSA-2.4 N1 — Hide empty + Reset rounding moved to the
                display-controls group at the left of the toolbar. */}
          </>
        )}
      </div>

      {/* FSA-2.4 N1 — Deviations panel. Consolidates every exception
          the auditor needs to act on into one collapsible strip.
          Auto-hides to "All checks passed" when items is empty.
          The drift items here are the same numbers that drive the
          sidebar Drift group + (eventually) the Submit-time block —
          single source so the auditor never sees inconsistent
          numbers across surfaces. N3 expands the item set with
          cross-note v2 + PL→Reserves signals. */}
      <div style={{ padding: '0 12px' }}>
        <NoteDeviationPanel items={deviationItems} />
      </div>

      {/* ADR-0006 Phase 1b (rev 4) — Depreciation Calc cross-check strips.
          Strictly INFORMATIONAL: they can never touch the note workspace,
          so they never dirty the note or block print preview. PPE /
          Intangibles get a per-cell comparison table; DT notes get the
          position/charge cross-check. Both self-hide when no saved
          depreciation calc exists. */}
      {(hydrate.scheduleCode === 'NOTE_PPE' || hydrate.scheduleCode === 'NOTE_INTANGIBLE_ASSETS')
        && multiColumnSpec && (
        <DepCalcSuggestionStrip
          engagementId={engagementId}
          scheduleId={hydrate.scheduleId}
          workspace={workspace}
          columnSpec={multiColumnSpec.columns}
          classNames={Object.fromEntries([
            ...renderableClasses.map(c => [c.classCode, c.className] as [string, string]),
            ...(workspace.added_classes ?? []).map(a => [a.local_id, a.name] as [string, string]),
          ])}
        />
      )}
      {(hydrate.scheduleCode === 'NOTE_DEFERRED_TAX' || hydrate.scheduleCode === 'NOTE_DEFERRED_TAX_EXPENSE') && (
        <DepCalcDtStrip
          engagementId={engagementId}
          scheduleCode={hydrate.scheduleCode}
          workspace={workspace}
        />
      )}

      {/* Inline add / rename custom-class form. Picking "Add Custom
          Class" from the header + Add menu reveals this form above the
          table. Rename uses the same form pre-filled with the existing
          name. Multi-column tables (PPE / Intangibles) trigger the same
          editor via the action menu on each class row. */}
      {customClassDraft && !readOnly && (
        <ClassLabelEditor
          draft={customClassDraft}
          anchorClasses={renderableClasses.map(s => ({
            classId: String(s.classId),
            className: s.className,
          }))}
          onChange={setCustomClassDraft}
          onConfirm={handleConfirmCustomClass}
          onCancel={handleCancelCustomClass}
        />
      )}

      <div style={{ flex: 1, overflowY: 'auto' }}>
        {isMultiColumn && multiColumnSpec ? (
          isSectionLayout ? (
            // Section-grid Notes (Owners' Capital Account): each top-level
            // class renders as its own collapsible band — "(a) Partners'
            // Capital Account", "(b) Partners' Current Account" — wrapping a
            // single-class grid with its own column headers, Total and
            // Previous Year Total. Reuses the Trade Payables Ageing band
            // pattern for consistency; the first section opens by default.
            <>
              {renderableClasses
                .filter(c => !c.parentClassId)
                .map((cls, i) => (
                  <AgeingSectionBand
                    key={cls.classCode}
                    noteName={hydrate.scheduleName ?? ''}
                    title={`(${String.fromCharCode(97 + i)}) ${cls.className}`}
                    defaultOpen={i === 0}
                  >
                    <MultiColumnClassTable
                      columnSpec={multiColumnSpec.columns}
                      classes={renderableClasses.filter(
                        c => c.classCode === cls.classCode || c.parentClassId === cls.classCode,
                      )}
                      addedClasses={[]}
                      workspace={workspace}
                      roundingFactorKey={hydrate.roundingFactorKey ?? 'ACTUALS'}
                      displayMode={displayMode}
                      setClassField={setClassField}
                      applyLedgerSnapshot={applyLedgerSnapshot}
                      setClassHideInPrint={setClassHideInPrint}
                      onStartRenameTemplate={handleStartRenameTemplate}
                      setLineItems={setLineItems}
                      customClassCodes={customClassCodes}
                      onStartRenameCustom={handleStartRenameCustom}
                      deleteCustomClass={deleteCustomClass}
                      groupingDimension={groupingDimension}
                      setClassDimensionValue={setClassDimensionValue}
                      setPriorYearTotal={setPriorYearTotal}
                      setColumnTotals={setColumnTotals}
                      readOnly={readOnly}
                      scheduleMetaDataJson={hydrate.scheduleMetaDataJson}
                      isCreditBalance={hydrate.isCreditBalance}
                      allowPriorYearInput={!hydrate.hasPriorEngagement}
                      bandClassCode={cls.classCode}
                      recomputeAppropriations={recomputeAppropriations}
                      partnerMasterPsr={partnerMasterPsr}
                    />
                  </AgeingSectionBand>
                ))}
            </>
          ) : scheduleDisplayOptions?.pyLayout === 'separate_grid' ? (
            // Ageing-matrix style: wrap CY in a date-titled band; render
            // a parallel PY band below. PY data entry grid is a planned
            // v2.1 follow-up — for now the band shows the prior period
            // end date so the structure + date plumbing are visible.
            <>
              {/* FSA-2.2 auto cross-note sweep section — sits above the
                  CY ageing band on Trade Payables / Trade Receivables.
                  Shows CY + PY sign-anomalous totals routed to the
                  Advances counterpart with a per-ledger exclude toggle.
                  Renders nothing when the schedule has no auto_sweep
                  config (LT borrowings, etc. still use the manual
                  cross-note flow in the line-item editor). */}
              {crossNoteConfig?.auto_sweep === 'sign_anomaly' && (
                <SignSweepSection
                  crossNoteConfig={crossNoteConfig}
                  isCreditBalance={hydrate.isCreditBalance}
                  liveClasses={hydrate.liveClasses}
                  consolidatedTbId={hydrate.consolidatedTrialBalanceId}
                  scheduleName={hydrate.scheduleName}
                  onExcludeChanged={refetchHydrate}
                  readOnly={readOnly}
                />
              )}
              <AgeingSectionBand
                noteName={hydrate.scheduleName ?? 'this Note'}
                periodEndDateIso={hydrate.periodEndDate}
                defaultOpen={true}
              >
                <MultiColumnClassTable
                  columnSpec={multiColumnSpec.columns}
                  classes={hydrate.liveClasses}
                  addedClasses={workspace.added_classes ?? []}
                  workspace={workspace}
                  roundingFactorKey={hydrate.roundingFactorKey ?? 'ACTUALS'}
                  displayMode={displayMode}
                  setClassField={setClassField}
                  applyLedgerSnapshot={applyLedgerSnapshot}
                  setClassHideInPrint={setClassHideInPrint}
                  onStartRenameTemplate={handleStartRenameTemplate}
                  setLineItems={setLineItems}
                  customClassCodes={customClassCodes}
                  onStartRenameCustom={handleStartRenameCustom}
                  deleteCustomClass={deleteCustomClass}
                  groupingDimension={groupingDimension}
                  setClassDimensionValue={setClassDimensionValue}
                  setPriorYearTotal={setPriorYearTotal}
                  setColumnTotals={setColumnTotals}
                  readOnly={readOnly}
                  scheduleMetaDataJson={hydrate.scheduleMetaDataJson}
                  isCreditBalance={hydrate.isCreditBalance}
                  allowPriorYearInput={!hydrate.hasPriorEngagement}
                />
              </AgeingSectionBand>
              <AgeingSectionBand
                noteName={hydrate.scheduleName ?? 'this Note'}
                periodEndDateIso={hydrate.priorPeriodEndDate}
                defaultOpen={false}
              >
                <MatrixPyGrid
                  columnSpec={multiColumnSpec.columns}
                  classes={hydrate.liveClasses}
                  workspace={workspace}
                  setClassField={setClassField}
                  roundingFactorKey={hydrate.roundingFactorKey ?? 'ACTUALS'}
                  displayMode={displayMode}
                  readOnly={readOnly}
                />
              </AgeingSectionBand>
            </>
          ) : (
            <MultiColumnClassTable
              columnSpec={multiColumnSpec.columns}
              classes={hydrate.liveClasses}
              addedClasses={workspace.added_classes ?? []}
              workspace={workspace}
              roundingFactorKey={hydrate.roundingFactorKey ?? 'ACTUALS'}
              displayMode={displayMode}
              setClassField={setClassField}
              applyLedgerSnapshot={applyLedgerSnapshot}
              setClassHideInPrint={setClassHideInPrint}
              onStartRenameTemplate={handleStartRenameTemplate}
              setLineItems={setLineItems}
              customClassCodes={customClassCodes}
              onStartRenameCustom={handleStartRenameCustom}
              deleteCustomClass={deleteCustomClass}
              groupingDimension={groupingDimension}
              setClassDimensionValue={setClassDimensionValue}
              setPriorYearTotal={setPriorYearTotal}
              setColumnTotals={setColumnTotals}
              readOnly={readOnly}
              scheduleMetaDataJson={hydrate.scheduleMetaDataJson}
              isCreditBalance={hydrate.isCreditBalance}
              allowPriorYearInput={!hydrate.hasPriorEngagement}
            />
          )
        ) : renderableClasses.length === 0 ? (
          // No classes on this Note — all data lives in additional
          // disclosures (e.g. Note 35 EPS post-rewrite). Render an
          // empty-state hint instead of a hollow class table; auditor
          // navigates to the Additional Disclosures tab to enter data.
          <div style={emptyClassesHintStyle}>
            <div style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: 6 }}>
              No classes on this Note.
            </div>
            <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
              All data for this Note lives in <strong>Additional Disclosures</strong> →
            </div>
          </div>
        ) : (
        <table style={tableStyle}>
          <thead style={theadStyle}>
            <tr>
              <th style={thStyle}>Class / Line Item</th>
              <th style={{ ...thStyle, textAlign: 'right', width: 160 }}>
                CY {displayMode === 'rounded' ? `Rounded` : `Actual`}
              </th>
              <th style={{ ...thStyle, textAlign: 'right', width: 160 }}>
                PY {displayMode === 'rounded' ? `Rounded` : `Actual`}
              </th>
              <th style={{ ...thStyle, width: 60 }} />
            </tr>
          </thead>
          <tbody>
            {/* FSA-2.2 sub-head grouping (Wave 1): when the schedule has a
                grouping_dimension, render flat-by-group rows (Secured /
                Unsecured / Untagged with subtotals). Each row is either a
                line item (carrying its dimension_value) or a class with no
                line items. The Note's tfoot total stays a sum of every
                saved class amount across all groups. When no grouping
                dimension, the existing flat-by-class render below runs. */}
            {groupedRenderPlan && (() => {
              // FSA-2.2 Wave 1 (#6 fix): ensure the inline line-item editor
              // renders ONCE at the first position matching `editing` —
              // even when a class spans multiple groups, the editor doesn't
              // duplicate. Edit on a specific line → editor immediately
              // below that line. Add from a class → editor immediately
              // below the class section in that group.
              let editorRendered = false;
              const renderEditorIfMatching = (matchesNow: boolean): React.ReactNode => {
                if (!matchesNow || editorRendered || !editing || readOnly) return null;
                const editingLc = renderableClasses.find(c => c.classCode === editing.classCode);
                const editingCd = workspace.class_data[editing.classCode];
                if (!editingLc || !editingCd) return null;
                const refIds = new Set<string>();
                for (const li of editingCd.line_items ?? []) {
                  if (li.allocation_mode === 'ledgers') {
                    for (const lref of li.ledgers) {
                      if (lref.ledger_id != null) refIds.add(String(lref.ledger_id));
                    }
                  }
                }
                editorRendered = true;
                // Per-session unique key forces React to remount the
                // editor on each new edit click — otherwise its
                // useState(draft.X) initialisers stay frozen on the
                // first opened item and the next Edit click shows
                // stale fields (cross-note value etc.).
                const editorSessionKey =
                  editing.mode === 'edit'
                    ? `edit-${editing.localId}`
                    : `add-${editing.classCode}`;
                return (
                  <tr key={`grouped-inline-editor-${editorSessionKey}`}>
                    <td colSpan={4} style={editorTableRowStyle}>
                      <LineItemEditor
                        key={editorSessionKey}
                        className={editingLc.className}
                        ledgers={editingLc.ledgers}
                        referencedLedgerIds={refIds}
                        editingLocalId={editing.mode === 'edit' ? editing.localId : null}
                        draft={editing.draft}
                        invertSign={invertSign}
                        groupingDimension={groupingDimension}
                        crossNoteConfig={crossNoteConfig}
                        forceManualOnly={isMemoOnly(editingLc.classCode)}
                        forceRenameOnly={editing.mode === 'edit' && editing.renameOnly}
                        onCancel={() => setEditing(null)}
                        onSave={draft => handleSaveLine(editingLc.classCode, editingLc.ledgers, draft)}
                      />
                    </td>
                  </tr>
                );
              };

              return groupedRenderPlan.map(group => (
              <React.Fragment key={`group-${group.optionCode ?? 'untagged'}`}>
                <tr style={groupHeaderRowStyle}>
                  <td colSpan={4} style={groupHeaderTdStyle}>
                    <span style={groupHeaderLabelStyle}>
                      {group.optionLabel}
                    </span>
                    {group.optionCode === null && (
                      <span style={groupHeaderWarnStyle} title="Line items without a dimension value land here. Tag each one via the row action menu to move it into the right sub-head.">
                        ⚠ untagged
                      </span>
                    )}
                  </td>
                </tr>
                {group.classes.map(classEntry => {
                  const lc = classEntry.lc;
                  const cd = classEntry.cd;

                  // Drift compute (same gross-equivalent formula as the
                  // flat render). When the class spans multiple groups,
                  // drift is still a class-level concept so the badge
                  // shows the same value in each appearance.
                  const liveTplCdr = hydrate.liveClasses.find(c => c.classCode === lc.classCode);
                  const liveCustCdr = liveCustomByLocalId.get(lc.classCode);
                  const liveClosingCdr = liveTplCdr?.liveClosingActual ?? liveCustCdr?.liveClosingActual ?? 0;
                  const liveOpeningCdr = liveTplCdr?.liveOpeningActual ?? liveCustCdr?.liveOpeningActual ?? 0;
                  const cdLineCrossCy = (cd.line_items ?? []).reduce((s, l) => s + (l.cross_note_amount ?? 0), 0);
                  const cdLineCrossPy = (cd.line_items ?? []).reduce((s, l) => s + (l.prior_cross_note_amount ?? 0), 0);
                  const cdGrossCy = (cd.amount_actual ?? 0) + (cd.cross_note_amount ?? 0) + cdLineCrossCy;
                  const cdGrossPy = (cd.prior_amount_actual ?? 0) + (cd.prior_cross_note_amount ?? 0) + cdLineCrossPy;
                  const cdClosingDrift = +(cdGrossCy - liveClosingCdr).toFixed(2);
                  const cdOpeningDrift = +(cdGrossPy - liveOpeningCdr).toFixed(2);
                  // Drift only makes sense for TB-mappable classes.
                  // MANUAL_DISCLOSURE (auditor types directly) and
                  // is_computed (value derived from rule) classes have
                  // no live TB source to drift against, so suppress.
                  const cdHasDrift = !isManualClass(lc.classRole)
                    && !lc.isComputed
                    && (Math.abs(cdClosingDrift) > 0.005 || Math.abs(cdOpeningDrift) > 0.005);
                  const cdHidden = cd.hide_in_print === true;
                  const cdIsCustom = customClassCodes.has(lc.classCode);
                  const cdRenamed = !cdIsCustom && hasLabelOverride(lc.classCode);
                  const cdDisplayName = cdIsCustom
                    ? lc.className
                    : resolveClassLabel(lc.classCode, lc.className);
                  const cdRemarkVisible = isClassTextVisible(lc.classCode);
                  const currentDimValue =
                    (groupingDimension && cd.dimension_values?.[groupingDimension.code]) ?? null;

                  // Shared chips strip on the class header / class-direct row.
                  const headerChips = (
                    <>
                      {cdIsCustom && (
                        <CustomClassChip tooltip="Engagement-time custom class. Rename or remove via the row action menu." />
                      )}
                      {cdRenamed && (
                        <span style={renamedBadgeStyle} title={`Template label: "${lc.className}". Rename via the row action menu (reset clears the override).`}>
                          <IconSystem.edit size={9} /> renamed
                        </span>
                      )}
                      {cdHidden && (
                        <span style={hiddenBadgeStyle} title="This class is hidden from the printed PDF. Toggle via the row action menu.">
                          <IconSystem.eyeOff size={10} /> hidden in print
                        </span>
                      )}
                      {/* FSA-2.4 N1 — class-level drift demoted from
                          red chip to muted amber dot. Note-level
                          drift remains the primary signal (Deviations
                          panel + sidebar Drift group); class-level
                          stays on-hover for the auditor who wants
                          to drill down to which class drifted. */}
                      {cdHasDrift && (
                        <span
                          style={classDriftDotStyle}
                          title={`Class drift — saved amount is out of sync with the live trial balance. CY drift: ${fmtAmt(cdClosingDrift)}, PY drift: ${fmtAmt(cdOpeningDrift)}. Use the row action menu → Update from Ledger to refresh.`}
                          aria-label={`Class drift ${fmtAmt(cdClosingDrift)}`}
                        >
                          ●
                        </span>
                      )}
                    </>
                  );

                  // Shared base action-menu items used by BOTH class-direct
                  // and class-with-line-items rows. Differences (Set
                  // Security only on class-direct) get appended at the call
                  // site.
                  const baseClassActions: any[] = [
                    {
                      id: 'update-from-ledger',
                      label: cdHasDrift ? 'Update from Ledger…' : 'Update from Ledger (no drift)',
                      icon: <IconSystem.refresh size={14} />,
                      onClick: () => applyLedgerSnapshot(lc.classCode),
                      disabled: !cdHasDrift,
                    },
                    {
                      id: 'add-line-item',
                      label: 'Add Line Item',
                      icon: <IconSystem.list size={14} />,
                      onClick: () => handleAddLine(lc.classCode),
                    },
                    {
                      id: 'add-class-text',
                      label: isClassTextVisible(lc.classCode) ? 'Class Text (visible)' : 'Add Class Text',
                      icon: <IconSystem.file size={14} />,
                      onClick: () => handleRevealClassText(lc.classCode),
                      disabled: isClassTextVisible(lc.classCode),
                    },
                    {
                      id: 'toggle-hide-in-print',
                      label: cdHidden ? 'Show in PDF' : 'Hide from PDF',
                      icon: cdHidden ? <IconSystem.eye size={14} /> : <IconSystem.eyeOff size={14} />,
                      onClick: () => setClassHideInPrint(lc.classCode, !cdHidden),
                    },
                    ...(cdIsCustom
                      ? [
                          {
                            id: 'rename-custom',
                            label: 'Rename Class',
                            icon: <IconSystem.edit size={14} />,
                            onClick: () => handleStartRenameCustom(lc.classCode, lc.className),
                          },
                          {
                            id: 'delete-custom',
                            label: 'Delete Custom Class',
                            icon: <IconSystem.trash size={14} />,
                            variant: 'danger' as const,
                            onClick: () => deleteCustomClass(lc.classCode),
                          },
                        ]
                      : [
                          {
                            id: 'rename-template',
                            label: cdRenamed ? 'Edit Rename' : 'Rename Class',
                            icon: <IconSystem.edit size={14} />,
                            onClick: () => handleStartRenameTemplate(lc.classCode, cdDisplayName),
                          },
                          ...(cdRenamed
                            ? [
                                {
                                  id: 'reset-rename',
                                  label: 'Reset to Template Label',
                                  icon: <IconSystem.refresh size={14} />,
                                  onClick: () => setClassLabelOverride(lc.classCode, null),
                                },
                              ]
                            : []),
                        ]),
                  ];

                  // Class-level dimension setter. Surfaced on every
                  // class row whenever a grouping dimension is active —
                  // both class-direct (hasOwnRow) AND class-with-line-
                  // items rows. The class value acts as the default for
                  // any child line item that lacks its own dim value
                  // (see the inheritance branch in the bucketing pass
                  // above); source-owned lines (cross_note / pl_flow)
                  // materialise without per-line dims, so this is the
                  // only place the auditor can place them under
                  // Secured / Unsecured.
                  const setSecurityActions: any[] = groupingDimension
                    ? [
                        ...groupingDimension.options.map(opt => ({
                          id: `set-dim-${opt.code}`,
                          label: `${groupingDimension.label}: ${opt.label}${currentDimValue === opt.code ? ' ✓' : ''}`,
                          icon: <IconSystem.list size={14} />,
                          onClick: () => setClassDimensionValue(lc.classCode, groupingDimension.code, opt.code),
                          disabled: currentDimValue === opt.code,
                        })),
                        {
                          id: 'set-dim-untagged',
                          label: `${groupingDimension.label}: Untagged${currentDimValue == null ? ' ✓' : ''}`,
                          icon: <IconSystem.refresh size={14} />,
                          onClick: () => setClassDimensionValue(lc.classCode, groupingDimension.code, null),
                          disabled: currentDimValue == null,
                        },
                      ]
                    : [];

                  // ───────────────────────────────────────────────────────
                  // hasOwnRow — class with no line items in this group.
                  // The class itself is the row; amount cells show the
                  // saved class amount; tag lives at class level.
                  // ───────────────────────────────────────────────────────
                  if (classEntry.hasOwnRow) {
                    return (
                      <React.Fragment key={`gcl-frag-${group.optionCode}-${lc.classCode}`}>
                        <tr style={cdHidden ? { ...classRowStyle, ...hiddenClassRowStyle } : classRowStyle}>
                          <td style={tdStyle}>
                            <div style={classNameRowStyle}>
                              <span style={{ fontWeight: 700 }}>{cdDisplayName}</span>
                              {headerChips}
                            </div>
                            <div style={classMetaStyle}>
                              {lc.classCode}{' · '}{lc.ledgerCount} ledger{lc.ledgerCount === 1 ? '' : 's'}
                            </div>
                          </td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>
                            <AmountCell
                              displayMode={displayMode}
                              actual={cd.amount_actual}
                              rounded={roundingExemptByCode.get(lc.classCode) ? cd.amount_actual : cd.amount_rounded}
                              autoRounded={roundingExemptByCode.get(lc.classCode) ? cd.amount_actual : cd.amount_rounded_auto}
                              outOfTolerance={roundingExemptByCode.get(lc.classCode) ? false : Math.abs(cd.amount_rounded - cd.amount_rounded_auto) > tolerance}
                              readOnly={readOnly}
                              invertSign={invertSign}
                              onRoundedChange={v => setClassField(lc.classCode, 'amount_rounded', v)}
                              onActualChange={isManualClass(lc.classRole) && !lc.isComputed ? v => setClassActual(lc.classCode, 'amount_actual', v) : undefined}
                            />
                          </td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>
                            <AmountCell
                              displayMode={displayMode}
                              actual={cd.prior_amount_actual}
                              rounded={roundingExemptByCode.get(lc.classCode) ? cd.prior_amount_actual : cd.prior_amount_rounded}
                              autoRounded={roundingExemptByCode.get(lc.classCode) ? cd.prior_amount_actual : cd.prior_amount_rounded_auto}
                              outOfTolerance={false}
                              readOnly={readOnly}
                              invertSign={invertSign}
                              onRoundedChange={v => setClassField(lc.classCode, 'prior_amount_rounded', v)}
                              onActualChange={isManualClass(lc.classRole) && !lc.isComputed ? v => setClassActual(lc.classCode, 'prior_amount_actual', v) : undefined}
                            />
                          </td>
                          <td style={{ ...tdStyle, textAlign: 'right', width: 60 }}>
                            {!readOnly && (
                              <ActionMenu
                                items={[...baseClassActions, ...setSecurityActions]}
                                position="bottom-right"
                              />
                            )}
                          </td>
                        </tr>
                        {/* Editor inline after class-direct row when adding line item to this class. */}
                        {renderEditorIfMatching(
                          !!editing
                          && editing.classCode === lc.classCode
                          && editing.mode === 'add',
                        )}
                        {cdRemarkVisible && (
                          <tr style={remarkRowStyle}>
                            <td colSpan={4} style={remarkTdStyle}>
                              <div style={remarkLabelRowStyle}>
                                <IconSystem.file size={11} />
                                <span style={remarkLabelStyle}>Class Text</span>
                                <span style={remarkHintStyle}>prints under the class name in the PDF</span>
                              </div>
                              <textarea
                                className="form-input"
                                rows={2}
                                value={cd.remarks ?? ''}
                                readOnly={readOnly}
                                disabled={readOnly}
                                onChange={e => setClassField(lc.classCode, 'remarks', e.target.value)}
                                placeholder='Free text printed under the class header'
                                style={remarkTextareaStyle}
                                autoFocus={classTextOpen.has(lc.classCode) && !cd.remarks}
                              />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  }

                  // ───────────────────────────────────────────────────────
                  // hasOwnRow=false — class header + filtered line items
                  // (within this group) + class subtotal row. Same class
                  // can repeat in multiple groups; each appearance shows
                  // its line items belonging to that group only.
                  // ───────────────────────────────────────────────────────
                  return (
                    <React.Fragment key={`gch-frag-${group.optionCode}-${lc.classCode}`}>
                      {/* Class header — no amount columns; line items below carry the values. */}
                      <tr style={cdHidden ? { ...classRowStyle, ...hiddenClassRowStyle } : classRowStyle}>
                        <td style={tdStyle}>
                          <div style={classNameRowStyle}>
                            <span style={{ fontWeight: 700 }}>{cdDisplayName}</span>
                            {headerChips}
                          </div>
                          <div style={classMetaStyle}>
                            {lc.classCode}{' · '}{lc.ledgerCount} ledger{lc.ledgerCount === 1 ? '' : 's'}
                          </div>
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'right', color: 'var(--text-secondary)', fontStyle: 'italic', fontSize: '0.72rem' }}>—</td>
                        <td style={{ ...tdStyle, textAlign: 'right', color: 'var(--text-secondary)', fontStyle: 'italic', fontSize: '0.72rem' }}>—</td>
                        <td style={{ ...tdStyle, textAlign: 'right', width: 60 }}>
                          {!readOnly && (
                            <ActionMenu items={[...baseClassActions, ...setSecurityActions]} position="bottom-right" />
                          )}
                        </td>
                      </tr>
                      {cdRemarkVisible && (
                        <tr style={remarkRowStyle}>
                          <td colSpan={4} style={remarkTdStyle}>
                            <div style={remarkLabelRowStyle}>
                              <IconSystem.file size={11} />
                              <span style={remarkLabelStyle}>Class Text</span>
                              <span style={remarkHintStyle}>prints under the class name in the PDF</span>
                            </div>
                            <textarea
                              className="form-input"
                              rows={2}
                              value={cd.remarks ?? ''}
                              readOnly={readOnly}
                              disabled={readOnly}
                              onChange={e => setClassField(lc.classCode, 'remarks', e.target.value)}
                              placeholder='Free text printed under the class header'
                              style={remarkTextareaStyle}
                              autoFocus={classTextOpen.has(lc.classCode) && !cd.remarks}
                            />
                          </td>
                        </tr>
                      )}
                      {/* Line items in this group, indented under the class. */}
                      {classEntry.lineItemsInGroup.map(li => {
                        const dimVal = groupingDimension && li.dimension_values?.[groupingDimension.code];
                        const liEditorKey = `${lc.classCode}::${li.local_id}`;
                        const liRemarkVisible = isLineItemTextVisible(lc.classCode, li.local_id);
                        return (
                          <React.Fragment key={`gli-frag-${group.optionCode}-${lc.classCode}-${li.local_id}`}>
                            <tr style={lineItemRowStyle}>
                              <td style={lineItemNameTdStyle}>
                                <span style={lineItemIndentStyle}>↳</span>
                                <span>{li.name}</span>
                                <span style={lineItemModeChipStyle}>{li.allocation_mode}</span>
                              </td>
                              <td style={{ ...tdStyle, textAlign: 'right' }}>
                                <AmountCell
                                  displayMode={displayMode}
                                  actual={li.amount_actual}
                                  rounded={li.amount_rounded}
                                  autoRounded={li.amount_rounded_auto}
                                  outOfTolerance={Math.abs(li.amount_rounded - li.amount_rounded_auto) > tolerance}
                                  readOnly={readOnly}
                                  invertSign={invertSign}
                                  onRoundedChange={v => setLineItemRounded(lc.classCode, li.local_id, 'amount_rounded', v)}
                                />
                              </td>
                              <td style={{ ...tdStyle, textAlign: 'right' }}>
                                <AmountCell
                                  displayMode={displayMode}
                                  actual={li.prior_amount_actual}
                                  rounded={li.prior_amount_rounded}
                                  autoRounded={li.prior_amount_rounded_auto}
                                  outOfTolerance={false}
                                  readOnly={readOnly}
                                  invertSign={invertSign}
                                  onRoundedChange={v => setLineItemRounded(lc.classCode, li.local_id, 'prior_amount_rounded', v)}
                                />
                              </td>
                              <td style={{ ...tdStyle, textAlign: 'right', width: 60 }}>
                                {!readOnly && (
                                  <ActionMenu
                                    items={[
                                      {
                                        id: 'edit',
                                        label: dimVal ? `Edit line item · ${dimVal}` : 'Edit line item',
                                        icon: <IconSystem.edit size={14} />,
                                        onClick: () => {
                                          const fullLi = (workspace.class_data[lc.classCode]?.line_items ?? []).find(x => x.local_id === li.local_id);
                                          if (fullLi) handleEditLine(lc.classCode, fullLi);
                                        },
                                      },
                                      {
                                        id: 'add-text',
                                        label: isLineItemTextVisible(lc.classCode, li.local_id)
                                          ? 'Text (visible)'
                                          : 'Add Text',
                                        icon: <IconSystem.file size={14} />,
                                        onClick: () => handleRevealLineItemText(lc.classCode, li.local_id),
                                        disabled: isLineItemTextVisible(lc.classCode, li.local_id),
                                      },
                                      {
                                        id: 'delete',
                                        label: 'Delete line item',
                                        icon: <IconSystem.trash size={14} />,
                                        variant: 'danger',
                                        onClick: () => handleDeleteLine(lc.classCode, li.local_id),
                                      },
                                    ]}
                                    position="bottom-right"
                                  />
                                )}
                              </td>
                            </tr>
                            {/* Inline editor RIGHT AFTER the row being edited (#6 fix). */}
                            {renderEditorIfMatching(
                              !!editing
                              && editing.classCode === lc.classCode
                              && editing.mode === 'edit'
                              && editing.localId === li.local_id,
                            )}
                            {liRemarkVisible && (
                              <tr style={remarkRowStyle}>
                                <td colSpan={4} style={lineItemRemarkTdStyle}>
                                  <div style={remarkLabelRowStyle}>
                                    <span style={lineItemIndentStyle}>↳</span>
                                    <IconSystem.file size={11} />
                                    <span style={remarkLabelStyle}>Line Item Text — {li.name}</span>
                                    <span style={remarkHintStyle}>prints below the line item in the PDF</span>
                                  </div>
                                  <textarea
                                    className="form-input"
                                    rows={2}
                                    value={li.remarks ?? ''}
                                    readOnly={readOnly}
                                    disabled={readOnly}
                                    onChange={e => setLineItemRemark(lc.classCode, li.local_id, e.target.value)}
                                    placeholder='Free text for this line item'
                                    style={remarkTextareaStyle}
                                    autoFocus={lineItemTextOpen.has(liEditorKey) && !li.remarks}
                                  />
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                      {/* Inline editor after the class section when adding a new line item. */}
                      {renderEditorIfMatching(
                        !!editing
                        && editing.classCode === lc.classCode
                        && editing.mode === 'add',
                      )}
                      {/* Class subtotal within this group. Display mode
                          picks between actual and rounded subtotals so
                          the Rounded tab honours the engagement factor. */}
                      <tr style={classSubtotalInGroupRowStyle}>
                        <td style={classSubtotalInGroupTdStyle}>
                          <span style={{ paddingLeft: 16 }}>Subtotal — {cdDisplayName}</span>
                        </td>
                        <td style={{ ...classSubtotalInGroupTdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                          {fmtAmt(displaySigned(
                            displayMode === 'rounded'
                              ? classEntry.classSubtotalCyRounded
                              : classEntry.classSubtotalCy,
                          ))}
                        </td>
                        <td style={{ ...classSubtotalInGroupTdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                          {fmtAmt(displaySigned(
                            displayMode === 'rounded'
                              ? classEntry.classSubtotalPyRounded
                              : classEntry.classSubtotalPy,
                          ))}
                        </td>
                        <td style={classSubtotalInGroupTdStyle} />
                      </tr>
                    </React.Fragment>
                  );
                })}
                {/* Group subtotal — display-mode aware. */}
                <tr style={groupSubtotalRowStyle}>
                  <td style={groupSubtotalTdStyle}>Subtotal — {group.optionLabel}</td>
                  <td style={{ ...groupSubtotalTdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {fmtAmt(displaySigned(
                      displayMode === 'rounded' ? group.subtotalCyRounded : group.subtotalCy,
                    ))}
                  </td>
                  <td style={{ ...groupSubtotalTdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {fmtAmt(displaySigned(
                      displayMode === 'rounded' ? group.subtotalPyRounded : group.subtotalPy,
                    ))}
                  </td>
                  <td style={groupSubtotalTdStyle} />
                </tr>
              </React.Fragment>
              ));
            })()}
            {!groupedRenderPlan && renderableClasses.map(lc => {
              // FSA-2.2 Wave 3: render the class row even when workspace
              // has no saved entry for it. A target Note that's never been
              // worked on (no class_data populated) still needs to surface
              // incoming cross-note refs from other Notes — those refs are
              // attached to the class row, so returning null here loses
              // them entirely. Synth a zero-amount cd so the render path
              // runs uniformly; the row will look empty except for the
              // incoming-from-other-Notes panel.
              const savedCd = workspace.class_data[lc.classCode];
              const incomingForThisClass =
                incomingCrossNoteByTargetClass.get(lc.classCode) ?? [];
              if (!savedCd && incomingForThisClass.length === 0) return null;
              const cd: NoteClassData = savedCd ?? {
                amount_actual: 0,
                amount_rounded_auto: 0,
                amount_rounded: 0,
                prior_amount_actual: 0,
                prior_amount_rounded_auto: 0,
                prior_amount_rounded: 0,
                ledgers: [],
                line_items: [],
              };

              const closingDrift = Math.abs(cd.amount_rounded - cd.amount_rounded_auto);
              const closingOutOfTolerance = closingDrift > tolerance;

              // FSA-2.2 saved-as-primary: ledger drift = |saved actual −
              // live TB actual| per class. Surfaces a badge so the auditor
              // knows the saved snapshot is out of sync with the current
              // TB. Comparison uses the same NoteClassLive / liveCustom
              // lookup the synthetic-class merge uses.
              const liveTemplateForRow = hydrate.liveClasses.find(c => c.classCode === lc.classCode);
              const liveCustomForRow = liveCustomByLocalId.get(lc.classCode);
              const liveClosingForRow =
                liveTemplateForRow?.liveClosingActual
                ?? liveCustomForRow?.liveClosingActual
                ?? 0;
              const liveOpeningForRow =
                liveTemplateForRow?.liveOpeningActual
                ?? liveCustomForRow?.liveOpeningActual
                ?? 0;
              // FSA-2.2 Wave 2: gross-equivalent for drift = saved
              // long-term (amount_actual) + saved cross-note + line
              // items' cross-note. amount_actual itself is already the
              // class total (Σ line items) when line items exist —
              // matches the existing render logic.
              //
              // FSA-2.4 N3 FIX (2026-06-21): the previous formula
              // assumed every class is a SOURCE for cross-note routing
              // (subtract outgoing → match live TB). It didn't account
              // for the DESTINATION side — where the class amount IS
              // an inflow materialized as a cross_note / pl_flow line
              // item with no direct TB backing. Result: target classes
              // like "Advances from Customers" (receiving sign-sweep
              // from Trade Payables) showed false drift = the entire
              // inflow amount (e.g. ₹5.83 Cr vs live ledger 0).
              //
              // Fix: subtract incoming-materialized line items from the
              // saved gross before comparing to live TB. Cross-note and
              // pl_flow line items represent inflows that already sit
              // in cd.amount_actual; for ledger-drift purposes they
              // shouldn't be expected to match TB on THIS class — they
              // match TB on the SOURCE class instead. The Effective
              // Ledger formula in EngagementNoteDataService.cs already
              // handles this at the Note level; this is the per-class
              // equivalent for the class-row drift dot.
              const lineItemsForCrossNote = cd.line_items ?? [];
              const savedLineCrossNoteCy = lineItemsForCrossNote.reduce(
                (s, li) => s + (li.cross_note_amount ?? 0), 0,
              );
              const savedLineCrossNotePy = lineItemsForCrossNote.reduce(
                (s, li) => s + (li.prior_cross_note_amount ?? 0), 0,
              );
              // Incoming inflows materialized as line items on this
              // class. They contribute to cd.amount_actual but have no
              // live TB backing — exclude from the "matches live TB?"
              // comparison.
              const incomingLineItemsSumCy = lineItemsForCrossNote
                .filter(li => li.allocation_mode === 'cross_note' || li.allocation_mode === 'pl_flow')
                .reduce((s, li) => s + (li.amount_actual ?? 0), 0);
              const incomingLineItemsSumPy = lineItemsForCrossNote
                .filter(li => li.allocation_mode === 'cross_note' || li.allocation_mode === 'pl_flow')
                .reduce((s, li) => s + (li.prior_amount_actual ?? 0), 0);
              const savedGrossCy =
                (cd.amount_actual ?? 0)
                + (cd.cross_note_amount ?? 0)
                + savedLineCrossNoteCy
                - incomingLineItemsSumCy;
              const savedGrossPy =
                (cd.prior_amount_actual ?? 0)
                + (cd.prior_cross_note_amount ?? 0)
                + savedLineCrossNotePy
                - incomingLineItemsSumPy;
              const closingLedgerDrift = +(savedGrossCy - liveClosingForRow).toFixed(2);
              const openingLedgerDrift = +(savedGrossPy - liveOpeningForRow).toFixed(2);
              // Drift only makes sense for TB-mappable classes.
              // MANUAL_DISCLOSURE (auditor types directly) and
              // is_computed (value derived from rule) classes have
              // no live TB source to drift against, so suppress.
              const hasLedgerDrift =
                !isManualClass(lc.classRole) && !lc.isComputed &&
                (Math.abs(closingLedgerDrift) > 0.005 || Math.abs(openingLedgerDrift) > 0.005);

              // Reconciliation: when line items exist, their sum must
              // equal the class total. Drift surfaces as an amber badge
              // so the auditor catches under/over-allocation.
              //
              // FSA-2.2 (Inventory Changes / Note 28): when the class
              // carries a template `subtotal_formula` (e.g.
              // "OPENING - CLOSING"), the "expected sum" is the formula
              // result — NOT Σ line_items. Without this branch the drift
              // = `(Open + Close) − (Open − Close)` = `2 × Close`, which
              // shows a phantom red badge for correctly-saved Notes.
              const lineItems = cd.line_items ?? [];
              const hasLineItems = lineItems.length > 0;
              const lineItemSum = evalClassSubtotal(
                lineItems,
                'amount_actual',
                lc.subtotalFormula,
              );
              const reconDrift = hasLineItems
                ? +(cd.amount_actual - lineItemSum).toFixed(2)
                : 0;
              const reconMatches = Math.abs(reconDrift) <= RECON_TOLERANCE_ACTUAL;

              // FSA-2.4 N2 — Class-level live override for the class
              // total cell.
              //
              // FSA-2.4 N2 FIX (2026-06-21): only apply the live
              // override when the class has NO line items. When line
              // items exist, the class amount IS the sum of line items
              // (per the standard FSA rule: class total = Σ saved
              // line_items[*].amount_actual). Overriding that with the
              // live ledger sum would (a) ignore manual / cross_note /
              // pl_flow line items entirely and (b) silently replace
              // the auditor's curated total with a raw TB number — the
              // exact behaviour the auditor flagged on the "Advances
              // from Customers" class (saved cross-note line total
              // 5.83 Cr was being displayed as 0 because that class
              // has no direct ledgers). Drift between class total and
              // live ledger contribution still surfaces via the existing
              // closingLedgerDrift / hasLedgerDrift dot — display value
              // stays the auditor's line-item sum.
              //
              // For classes WITHOUT line items (direct ledger mapping),
              // live override still applies — class total is the live
              // ledger sum by definition, no line items to respect.
              let classLiveCy: number | undefined;
              let classLivePy: number | undefined;
              if (!readOnly
                  && !isManualClass(lc.classRole)
                  && !lc.isComputed
                  && !hasLineItems) {
                classLiveCy = liveClosingForRow
                  - (cd.cross_note_amount ?? 0)
                  - savedLineCrossNoteCy;
                classLivePy = liveOpeningForRow
                  - (cd.prior_cross_note_amount ?? 0)
                  - savedLineCrossNotePy;
              }

              const editingThisClass = editing?.classCode === lc.classCode;

              // Ledgers already referenced by an existing line_item in 'ledgers'
              // mode — disable them in the picker so a ledger can't be
              // allocated twice within the same class.
              const referencedLedgerIds = new Set<string>();
              for (const li of lineItems) {
                if (li.allocation_mode === 'ledgers') {
                  for (const lref of li.ledgers) {
                    if (lref.ledger_id != null) referencedLedgerIds.add(String(lref.ledger_id));
                  }
                }
              }

              const hidden = cd.hide_in_print === true;
              const isCustom = customClassCodes.has(lc.classCode);
              const renamed = !isCustom && hasLabelOverride(lc.classCode);
              const displayName = isCustom
                ? lc.className
                : resolveClassLabel(lc.classCode, lc.className);

              return (
                <React.Fragment key={lc.classCode}>
                  {/* ───── Class row ───── */}
                  <tr style={hidden ? { ...classRowStyle, ...hiddenClassRowStyle } : classRowStyle}>
                    <td style={tdStyle}>
                      <div style={classNameRowStyle}>
                        <span style={{ fontWeight: 700 }}>{displayName}</span>
                        {isCustom && (
                          <CustomClassChip tooltip="Engagement-time custom class. Rename or remove via the row action menu." />
                        )}
                        {renamed && (
                          <span
                            style={renamedBadgeStyle}
                            title={`Template label: "${lc.className}". Rename via the row action menu (reset clears the override).`}
                          >
                            <IconSystem.edit size={9} /> renamed
                          </span>
                        )}
                        {hidden && (
                          <span
                            style={hiddenBadgeStyle}
                            title="This class is hidden from the printed PDF. Toggle via the row action menu."
                          >
                            <IconSystem.eyeOff size={10} /> hidden in print
                          </span>
                        )}
                        {/* FSA-2.4 N1 — class-level drift demoted to dot. */}
                        {hasLedgerDrift && (
                          <span
                            style={classDriftDotStyle}
                            title={`Class drift — saved amount is out of sync with the live trial balance. CY drift: ${fmtAmt(closingLedgerDrift)}, PY drift: ${fmtAmt(openingLedgerDrift)}. Use the row action menu → Update from Ledger to refresh.`}
                            aria-label={`Class drift ${fmtAmt(closingLedgerDrift)}`}
                          >
                            ●
                          </span>
                        )}
                        {/* FSA-2.4 N1 — within-class line-items recon.
                            Silence = good (no green ✓ chip when
                            balanced — auditor scans the screen for
                            problems, not confirmations). Drift demoted
                            to muted amber dot consistent with class-
                            level + line-level drift treatment. */}
                        {hasLineItems && !reconMatches && (
                          <span
                            style={classDriftDotStyle}
                            title={`Line items sum to ${fmtAmt(displaySigned(lineItemSum))}, class total is ${fmtAmt(displaySigned(cd.amount_actual))}`}
                            aria-label={`Class internal drift ${fmtAmt(Math.abs(reconDrift))}`}
                          >
                            ●
                          </span>
                        )}
                        {/* "+ Add" affordance moved off the class name row
                            into the right-column three-dot action menu —
                            mirrors the line-item pattern. Keeps the class
                            row visually clean and consistent across rows. */}
                      </div>
                      <div style={classMetaStyle}>
                        {lc.classCode}
                        {' · '}{lc.ledgerCount} ledger{lc.ledgerCount === 1 ? '' : 's'}
                        {/* FSA-2.4 N1 — memo / manual / computed labels
                            consolidated. Previously a class could
                            simultaneously show "· memo (excluded from
                            total)" AND "· memo only (excluded from
                            Note Total)" — two phrasings of the same
                            fact (the screenshot's "recon · memo ·
                            memo only" stack came from this). Single
                            check now, single label per state. */}
                        {(() => {
                          if (lc.isComputed) {
                            const hint = computedRuleHint(lc.computationRuleJson);
                            return hint ? ` · ${hint}` : ' · computed';
                          }
                          if (isMemoOnly(lc.classCode) || (isManualClass(lc.classRole) && isMemoOnlyClass(lc.classRole))) {
                            return ' · memo (not in Note total)';
                          }
                          if (isManualClass(lc.classRole)) {
                            return ' · manual disclosure';
                          }
                          return null;
                        })()}
                      </div>
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      <AmountCell
                        displayMode={displayMode}
                        actual={cd.amount_actual}
                        rounded={roundingExemptByCode.get(lc.classCode) ? cd.amount_actual : cd.amount_rounded}
                        autoRounded={roundingExemptByCode.get(lc.classCode) ? cd.amount_actual : cd.amount_rounded_auto}
                        outOfTolerance={roundingExemptByCode.get(lc.classCode) ? false : closingOutOfTolerance}
                        readOnly={readOnly}
                        invertSign={invertSign}
                        onRoundedChange={v => setClassField(lc.classCode, 'amount_rounded', v)}
                        onActualChange={
                          isManualClass(lc.classRole) && !lc.isComputed
                            ? v => setClassActual(lc.classCode, 'amount_actual', v)
                            : undefined
                        }
                        liveActual={classLiveCy}
                      />
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      <AmountCell
                        displayMode={displayMode}
                        actual={cd.prior_amount_actual}
                        rounded={roundingExemptByCode.get(lc.classCode) ? cd.prior_amount_actual : cd.prior_amount_rounded}
                        autoRounded={roundingExemptByCode.get(lc.classCode) ? cd.prior_amount_actual : cd.prior_amount_rounded_auto}
                        outOfTolerance={false}
                        readOnly={readOnly}
                        invertSign={invertSign}
                        onRoundedChange={v => setClassField(lc.classCode, 'prior_amount_rounded', v)}
                        onActualChange={
                          isManualClass(lc.classRole) && !lc.isComputed
                            ? v => setClassActual(lc.classCode, 'prior_amount_actual', v)
                            : undefined
                        }
                        liveActual={classLivePy}
                      />
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right', width: 60 }}>
                      {!readOnly && !editingThisClass && (
                        <ActionMenu
                          items={[
                            // FSA-2.2 saved-as-primary: explicit refresh
                            // from live TB. Only useful when the saved
                            // snapshot has drifted; we keep the menu item
                            // visible always so the auditor can opt in
                            // even when drift is below the display
                            // threshold (rounding noise / equal sums).
                            {
                              id: 'update-from-ledger',
                              label: hasLedgerDrift
                                ? 'Update from Ledger…'
                                : 'Update from Ledger (no drift)',
                              icon: <IconSystem.refresh size={14} />,
                              onClick: () => applyLedgerSnapshot(lc.classCode),
                              disabled: !hasLedgerDrift,
                            },
                            {
                              id: 'add-line-item',
                              label: 'Add Line Item',
                              icon: <IconSystem.list size={14} />,
                              onClick: () => handleAddLine(lc.classCode),
                            },
                            {
                              id: 'add-class-text',
                              label: isClassTextVisible(lc.classCode)
                                ? 'Class Text (visible)'
                                : 'Add Class Text',
                              icon: <IconSystem.file size={14} />,
                              onClick: () => handleRevealClassText(lc.classCode),
                              disabled: isClassTextVisible(lc.classCode),
                            },
                            {
                              id: 'toggle-hide-in-print',
                              label: hidden ? 'Show in PDF' : 'Hide from PDF',
                              icon: hidden
                                ? <IconSystem.eye size={14} />
                                : <IconSystem.eyeOff size={14} />,
                              onClick: () => setClassHideInPrint(lc.classCode, !hidden),
                            },
                            // Custom-class-only actions — rename + delete.
                            // Hidden for template classes (they have no
                            // local_id and can't be renamed/deleted).
                            ...(isCustom
                              ? [
                                  {
                                    id: 'rename-custom',
                                    label: 'Rename Class',
                                    icon: <IconSystem.edit size={14} />,
                                    onClick: () =>
                                      handleStartRenameCustom(lc.classCode, lc.className),
                                  },
                                  {
                                    id: 'delete-custom',
                                    label: 'Delete Custom Class',
                                    icon: <IconSystem.trash size={14} />,
                                    variant: 'danger' as const,
                                    onClick: () => deleteCustomClass(lc.classCode),
                                  },
                                ]
                              : [
                                  // Template-class rename override:
                                  // saves a per-engagement label that
                                  // replaces the seed label in the
                                  // modal + PDF. "Reset" clears the
                                  // override so the seed label shows
                                  // again. Available only when an
                                  // override is currently active.
                                  {
                                    id: 'rename-template',
                                    label: renamed ? 'Edit Rename' : 'Rename Class',
                                    icon: <IconSystem.edit size={14} />,
                                    onClick: () =>
                                      handleStartRenameTemplate(lc.classCode, displayName),
                                  },
                                  ...(renamed
                                    ? [
                                        {
                                          id: 'reset-rename',
                                          label: 'Reset to Template Label',
                                          icon: <IconSystem.refresh size={14} />,
                                          onClick: () =>
                                            setClassLabelOverride(lc.classCode, null),
                                        },
                                      ]
                                    : []),
                                ]),
                          ]}
                          position="bottom-right"
                        />
                      )}
                    </td>
                  </tr>

                  {/* FSA-2.2 Wave 3 (A): cross_note-mode line items
                      render via the existing line-item map below — no
                      separate synthetic-row component. The hydrate
                      merge in FsaNoteModal materialises each incoming
                      ref into cd.line_items with allocation_mode =
                      'cross_note', so it shows up as a normal row with
                      a "cross_note" mode chip + source hint. */}

                  {/* ───── Class remark row (progressive disclosure) ───── */}
                  {isClassTextVisible(lc.classCode) && (
                    <tr style={remarkRowStyle}>
                      <td colSpan={4} style={remarkTdStyle}>
                        <div style={remarkLabelRowStyle}>
                          <IconSystem.file size={11} />
                          <span style={remarkLabelStyle}>Class Text</span>
                          <span style={remarkHintStyle}>
                            prints under the class name in the PDF
                          </span>
                        </div>
                        <textarea
                          className="form-input"
                          rows={2}
                          value={cd.remarks ?? ''}
                          readOnly={readOnly}
                          disabled={readOnly}
                          onChange={e => setClassField(lc.classCode, 'remarks', e.target.value)}
                          placeholder='e.g. "Secured against fixed deposits", "Includes leasehold improvements"'
                          style={remarkTextareaStyle}
                          autoFocus={classTextOpen.has(lc.classCode) && !cd.remarks}
                        />
                      </td>
                    </tr>
                  )}

                  {/* ───── Line item rows (indented siblings) ───── */}
                  {lineItems.map(li => {
                    // FSA-2.2 multi-ledger line items: show the first
                    // ledger inline and an "(+N more)" suffix when the
                    // line item is allocated to multiple ledgers.
                    const linkedLedgerName =
                      li.allocation_mode === 'ledgers' ? li.ledgers[0]?.ledger_name : null;
                    const extraLedgerCount =
                      li.allocation_mode === 'ledgers'
                        ? Math.max(0, li.ledgers.length - 1)
                        : 0;

                    // FSA-2.2 saved-as-primary: per-line-item drift —
                    // saved gross-equivalent (long-term + cross-note)
                    // vs live sum across the line item's allocated
                    // ledger ids. The cross-note amount is added back
                    // because amount_actual is the long-term portion;
                    // the live TB carries the gross. Orphaned ledger
                    // references contribute zero to live — matches what
                    // "Update from Ledger" will write.
                    //
                    // FSA-2.4 N2 — also compute LIVE PY sum + expose
                    // both as live overrides so the cell renders the
                    // current TB value instead of the saved snapshot.
                    // Only applies to `ledgers` mode (manual / cross_note
                    // / pl_flow have no "live" notion).
                    let liLedgerDrift = 0;
                    let orphanLedgerCount = 0;
                    let liLiveCy: number | undefined;
                    let liLivePy: number | undefined;
                    if (li.allocation_mode === 'ledgers' && li.ledgers.length > 0) {
                      const liveById = new Map(
                        lc.ledgers.map(l => [String(l.ledgerId), l]),
                      );
                      let liveCy = 0;
                      let livePy = 0;
                      for (const lref of li.ledgers) {
                        const live = lref.ledger_id != null
                          ? liveById.get(String(lref.ledger_id))
                          : undefined;
                        if (live) {
                          liveCy += live.closingActual;
                          livePy += live.openingActual;
                        } else {
                          orphanLedgerCount += 1;
                        }
                      }
                      const savedGross = (li.amount_actual ?? 0) + (li.cross_note_amount ?? 0);
                      liLedgerDrift = +(savedGross - liveCy).toFixed(2);
                      // Live override values for the cell renderer.
                      // Subtract the cross-note portion from CY/PY (the
                      // line item's saved amount excludes it; the
                      // displayed live amount should too — otherwise
                      // the cell would show the gross while the saved
                      // value below it shows the net, causing a
                      // confusing "drift" that's just the routing).
                      liLiveCy = liveCy - (li.cross_note_amount ?? 0);
                      liLivePy = livePy - (li.prior_cross_note_amount ?? 0);
                    }
                    const hasLineItemDrift = Math.abs(liLedgerDrift) > 0.005;

                    // FSA-2.2 Wave 3 (A): cross_note-mode uses the same
                    // row shape as ledger / manual. The mode chip
                    // (`cross_note`) is the only inline marker — the
                    // source attribution lives on source_ref for the
                    // PDF / drift detector but does NOT clutter the
                    // line label.
                    // cross_note + pl_flow lines are both source-owned —
                    // refresh via the bulk "Update from sources" action,
                    // never via per-line Edit/Delete (their amounts come
                    // from elsewhere).
                    const isCrossNote = li.allocation_mode === 'cross_note' || li.allocation_mode === 'pl_flow';
                    // FSA-2.2 — class-level cross-notes (auto sign-sweep
                    // targets: Advances to Suppliers / Advances from
                    // Customers) have no source line item to defer to.
                    // The auditor renames the materialized line directly
                    // here. Saved name persists across "Update from
                    // cross-notes" merges (saved-snapshot-wins).
                    const isCrossNoteFromClass = isCrossNote
                      && !li.source_ref?.line_item_local_id;
                    return (
                      <tr key={li.local_id} style={lineItemRowStyle}>
                        <td style={lineItemNameTdStyle}>
                          <span style={lineItemIndentStyle}>↳</span>
                          <span>{li.name}</span>
                          {linkedLedgerName && (
                            <span style={ledgerHintStyle}>
                              {' · '}{linkedLedgerName}
                              {extraLedgerCount > 0 && (
                                <span> (+{extraLedgerCount} more)</span>
                              )}
                            </span>
                          )}
                          <span style={lineItemModeChipStyle}>{li.allocation_mode}</span>
                          {/* FSA-2.4 N1 — line-item drift demoted from a
                              red chip with inline amount to a muted dot.
                              Auditors care about NOTE-level drift, not
                              line-level (line variances often offset
                              intentionally). Per-line signal stays
                              available on hover; the panel + sidebar
                              surface the aggregate that actually matters.
                              Dot click-target stays large enough for
                              touch via the wrapper span's padding. */}
                          {hasLineItemDrift && (
                            <span
                              style={lineItemDriftDotStyle}
                              title={
                                orphanLedgerCount > 0
                                  ? `${orphanLedgerCount} ledger${orphanLedgerCount === 1 ? '' : 's'} originally mapped to this line item ${orphanLedgerCount === 1 ? 'has' : 'have'} been reclassified to another class. Drift ₹ ${fmtAmt(liLedgerDrift)}. Use the class's Update from Ledger to drop the orphaned references.`
                                  : `Line-item drift ₹ ${fmtAmt(liLedgerDrift)} — saved total differs from live ledger sum. Use Update from Ledger to refresh.`
                              }
                              aria-label={`Line-item drift ${fmtAmt(liLedgerDrift)}`}
                            >
                              ●
                            </span>
                          )}
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>
                          <AmountCell
                            displayMode={displayMode}
                            actual={li.amount_actual}
                            rounded={li.amount_rounded}
                            autoRounded={li.amount_rounded_auto}
                            outOfTolerance={Math.abs(li.amount_rounded - li.amount_rounded_auto) > tolerance}
                            readOnly={readOnly}
                            invertSign={invertSign}
                            onRoundedChange={v => setLineItemRounded(lc.classCode, li.local_id, 'amount_rounded', v)}
                            liveActual={!readOnly ? liLiveCy : undefined}
                          />
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>
                          <AmountCell
                            displayMode={displayMode}
                            actual={li.prior_amount_actual}
                            rounded={li.prior_amount_rounded}
                            autoRounded={li.prior_amount_rounded_auto}
                            outOfTolerance={false}
                            readOnly={readOnly}
                            invertSign={invertSign}
                            onRoundedChange={v => setLineItemRounded(lc.classCode, li.local_id, 'prior_amount_rounded', v)}
                            liveActual={!readOnly ? liLivePy : undefined}
                          />
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'right', width: 60 }}>
                          {!readOnly && (() => {
                            // Reorder affordance — applies to every line
                            // item regardless of allocation_mode. Disable
                            // at the array edges. Position is auditor-
                            // controlled even for source-owned rows
                            // (cross_note / pl_flow); their amounts are
                            // still locked from edit/delete.
                            const liIdx = lineItems.findIndex(x => x.local_id === li.local_id);
                            const isFirst = liIdx <= 0;
                            const isLast = liIdx < 0 || liIdx >= lineItems.length - 1;
                            const moveItems = [
                              {
                                id: 'move-up',
                                label: 'Move Up',
                                icon: <IconSystem.chevronUp size={14} />,
                                onClick: () => handleMoveLine(lc.classCode, li.local_id, 'up'),
                                disabled: isFirst,
                              },
                              {
                                id: 'move-down',
                                label: 'Move Down',
                                icon: <IconSystem.chevronDown size={14} />,
                                onClick: () => handleMoveLine(lc.classCode, li.local_id, 'down'),
                                disabled: isLast,
                              },
                            ];
                            return (
                              <ActionMenu
                                items={
                                  // cross_note line items are owned by the
                                  // source Note — auditor refreshes them via
                                  // "Update from cross-notes" (bulk) rather
                                  // than edit/delete. Reorder is still
                                  // allowed (position belongs to the host
                                  // Note's presentation, not the source).
                                  //
                                  // Exception: class-level cross-notes (auto
                                  // sign-sweep targets) have no source line
                                  // item — the auditor renames the
                                  // materialized line directly here. Edit
                                  // opens the editor in rename-only mode;
                                  // Delete still blocked (delete would just
                                  // re-appear on next sweep refresh).
                                  isCrossNote && !isCrossNoteFromClass
                                    ? [
                                        ...moveItems,
                                        {
                                          id: 'add-text',
                                          label: isLineItemTextVisible(lc.classCode, li.local_id)
                                            ? 'Text (visible)'
                                            : 'Add Text',
                                          icon: <IconSystem.file size={14} />,
                                          onClick: () => handleRevealLineItemText(lc.classCode, li.local_id),
                                          disabled: isLineItemTextVisible(lc.classCode, li.local_id),
                                        },
                                      ]
                                    : isCrossNoteFromClass
                                    ? [
                                        {
                                          id: 'rename',
                                          label: 'Rename',
                                          icon: <IconSystem.edit size={14} />,
                                          onClick: () => handleEditLine(lc.classCode, li),
                                        },
                                        ...moveItems,
                                        {
                                          id: 'add-text',
                                          label: isLineItemTextVisible(lc.classCode, li.local_id)
                                            ? 'Text (visible)'
                                            : 'Add Text',
                                          icon: <IconSystem.file size={14} />,
                                          onClick: () => handleRevealLineItemText(lc.classCode, li.local_id),
                                          disabled: isLineItemTextVisible(lc.classCode, li.local_id),
                                        },
                                      ]
                                    : [
                                        {
                                          id: 'edit',
                                          label: 'Edit line item',
                                          icon: <IconSystem.edit size={14} />,
                                          onClick: () => handleEditLine(lc.classCode, li),
                                        },
                                        ...moveItems,
                                        {
                                          id: 'add-text',
                                          label: isLineItemTextVisible(lc.classCode, li.local_id)
                                            ? 'Text (visible)'
                                            : 'Add Text',
                                          icon: <IconSystem.file size={14} />,
                                          onClick: () => handleRevealLineItemText(lc.classCode, li.local_id),
                                          disabled: isLineItemTextVisible(lc.classCode, li.local_id),
                                        },
                                        {
                                          id: 'delete',
                                          label: 'Delete line item',
                                          icon: <IconSystem.trash size={14} />,
                                          variant: 'danger' as const,
                                          onClick: () => handleDeleteLine(lc.classCode, li.local_id),
                                        },
                                      ]
                                }
                                position="bottom-right"
                              />
                            );
                          })()}
                        </td>
                      </tr>
                    );
                  })}

                  {/* ───── Line-item remark rows (progressive disclosure) ───── */}
                  {lineItems.map(li => {
                    if (!isLineItemTextVisible(lc.classCode, li.local_id)) return null;
                    const editorKey = `${lc.classCode}::${li.local_id}`;
                    return (
                      <tr key={`remark-${li.local_id}`} style={remarkRowStyle}>
                        <td colSpan={4} style={lineItemRemarkTdStyle}>
                          <div style={remarkLabelRowStyle}>
                            <span style={lineItemIndentStyle}>↳</span>
                            <IconSystem.file size={11} />
                            <span style={remarkLabelStyle}>Line Item Text — {li.name}</span>
                            <span style={remarkHintStyle}>
                              prints below the line item in the PDF
                            </span>
                          </div>
                          <textarea
                            className="form-input"
                            rows={2}
                            value={li.remarks ?? ''}
                            readOnly={readOnly}
                            disabled={readOnly}
                            onChange={e => setLineItemRemark(lc.classCode, li.local_id, e.target.value)}
                            placeholder='Free text for this line item'
                            style={remarkTextareaStyle}
                            autoFocus={lineItemTextOpen.has(editorKey) && !li.remarks}
                          />
                        </td>
                      </tr>
                    );
                  })}

                  {/* ───── Inline editor row (only when adding/editing this class) ───── */}
                  {editingThisClass && (() => {
                    // See note on the grouped-render counterpart above —
                    // per-session unique key forces a fresh useState
                    // initialisation on every Edit click.
                    const flatEditorKey =
                      editing.mode === 'edit'
                        ? `edit-${editing.localId}`
                        : `add-${editing.classCode}`;
                    return (
                      <tr key={`flat-inline-editor-${flatEditorKey}`}>
                        <td colSpan={4} style={editorTableRowStyle}>
                          <LineItemEditor
                            key={flatEditorKey}
                            className={lc.className}
                            ledgers={lc.ledgers}
                            referencedLedgerIds={referencedLedgerIds}
                            editingLocalId={editing.mode === 'edit' ? editing.localId : null}
                            draft={editing.draft}
                            invertSign={invertSign}
                            groupingDimension={groupingDimension}
                            crossNoteConfig={crossNoteConfig}
                            forceManualOnly={isMemoOnly(lc.classCode)}
                            forceRenameOnly={editing.mode === 'edit' && editing.renameOnly}
                            onCancel={() => setEditing(null)}
                            onSave={draft => handleSaveLine(lc.classCode, lc.ledgers, draft)}
                          />
                        </td>
                      </tr>
                    );
                  })()}
                </React.Fragment>
              );
            })}
          </tbody>
          {showNoteTotal && (
            <tfoot>
              <tr>
                <td style={{ ...tdStyle, ...totalRowStyle, fontWeight: 700 }}>Total</td>
                <td style={{ ...tdStyle, ...totalRowStyle, textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                  {fmtAmt(displaySigned(displayMode === 'rounded' ? cRounded : cActual))}
                </td>
                <td style={{ ...tdStyle, ...totalRowStyle, textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                  {fmtAmt(displaySigned(displayMode === 'rounded' ? oRounded : oActual))}
                </td>
                <td style={{ ...tdStyle, ...totalRowStyle }} />
              </tr>
            </tfoot>
          )}
        </table>
        )}

        {/* Deliverable Note text — progressively disclosed. Hidden when
            empty until the auditor picks "Add Note Text" from the header
            "+ Add" dropdown. Once any content is saved, always visible
            (so re-opening the note shows what's there). */}
        {noteTextVisible && (
          <div style={noteTextBlockStyle}>
            <label style={noteTextLabelStyle}>
              Note Text
              <span style={noteTextHintStyle}>
                (prints below the table — visible in the deliverable PDF)
              </span>
            </label>
            <textarea
              className="form-input"
              rows={3}
              value={workspace.note_remark ?? ''}
              readOnly={readOnly}
              disabled={readOnly}
              onChange={e => setNoteRemark(e.target.value)}
              placeholder='e.g. "Net of TDS", "Includes inter-branch transfers", or any entity-specific Schedule III qualifier.'
              style={noteTextAreaStyle}
              autoFocus={noteTextOpen && !workspace.note_remark}
            />
          </div>
        )}
      </div>
    </div>
  );
};

// ───── compact reconciliation chip (toolbar) ────────────────────────────────
// Chip shows ✓ when both CY and PY drifts are within tolerance; otherwise
// shows the worst-case drift. Hover reveals the full TB Mapped / Note Total
// / Drift breakdown for both periods.

const ReconChip: React.FC<{
  allMatch: boolean;
  driftCy: number;
  driftPy: number;
  matchesCy: boolean;
  matchesPy: boolean;
  mappedCy: number;
  mappedPy: number;
  noteTotalCy: number;
  noteTotalPy: number;
  invertSign: boolean;
}> = ({
  allMatch, driftCy, driftPy, matchesCy, matchesPy,
  mappedCy, mappedPy, noteTotalCy, noteTotalPy, invertSign,
}) => {
  const [open, setOpen] = useState(false);
  const flip = (n: number) => invertSign ? -n : n;

  // Worst-case drift drives the chip label when not all match.
  const worstAbs = Math.max(Math.abs(driftCy), Math.abs(driftPy));
  const chipLabel = allMatch
    ? '✓ Reconciled'
    : `⚠ Drift ${fmtAmt(worstAbs)}`;
  const chipColors = allMatch
    ? { bg: '#dcfce7', fg: '#166534', border: '#bbf7d0' }
    : { bg: '#fef3c7', fg: '#92400e', border: '#fde68a' };

  return (
    <span
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
      }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          padding: '2px 8px',
          borderRadius: 999,
          background: chipColors.bg,
          color: chipColors.fg,
          border: `1px solid ${chipColors.border}`,
          fontSize: '0.7rem',
          fontWeight: 700,
          letterSpacing: 0.3,
          cursor: 'help',
        }}
      >
        {chipLabel}
      </span>
      {open && (
        <div style={reconTooltipStyle} role="tooltip">
          <div style={reconTooltipHeaderStyle}>Note Reconciliation</div>
          <table style={reconTooltipTableStyle}>
            <thead>
              <tr>
                <th style={reconTooltipThStyle} />
                <th style={reconTooltipThNumStyle}>CY</th>
                <th style={reconTooltipThNumStyle}>PY</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={reconTooltipLabelTdStyle}>TB Mapped</td>
                <td style={reconTooltipNumTdStyle}>{fmtAmt(flip(mappedCy))}</td>
                <td style={reconTooltipNumTdStyle}>{fmtAmt(flip(mappedPy))}</td>
              </tr>
              <tr>
                <td style={reconTooltipLabelTdStyle}>Note Total</td>
                <td style={reconTooltipNumTdStyle}>{fmtAmt(flip(noteTotalCy))}</td>
                <td style={reconTooltipNumTdStyle}>{fmtAmt(flip(noteTotalPy))}</td>
              </tr>
              <tr style={reconTooltipDriftRowStyle}>
                <td style={reconTooltipLabelTdStyle}>Drift</td>
                <td
                  style={{
                    ...reconTooltipNumTdStyle,
                    color: matchesCy ? '#166534' : '#92400e',
                  }}
                >
                  {matchesCy ? '✓ Matches' : `⚠ ${fmtAmt(flip(driftCy))}`}
                </td>
                <td
                  style={{
                    ...reconTooltipNumTdStyle,
                    color: matchesPy ? '#166534' : '#92400e',
                  }}
                >
                  {matchesPy ? '✓ Matches' : `⚠ ${fmtAmt(flip(driftPy))}`}
                </td>
              </tr>
            </tbody>
          </table>
          {!allMatch && (
            <div style={reconTooltipHintStyle}>
              Causes:&nbsp;
              <strong>(a)</strong>&nbsp;TB entries on this schedule not assigned
              to a specific class — open <em>Trial Balance</em> →
              filter <em>Pending class split</em>.&nbsp;
              <strong>(b)</strong>&nbsp;Manual-class amounts under- or
              over-allocating the pool.
            </div>
          )}
        </div>
      )}
    </span>
  );
};

// ───── shared types ─────────────────────────────────────────────────────────

// Ledger / LineItemDraft / blankDraft / LineItemEditor / LedgerChipPicker
// were inline here previously. Extracted to ./shared/LineItemEditor.tsx so
// the multi-column class table can drive the SAME editor. Local aliases
// kept so this file's existing types and call sites compile unchanged.
type Ledger = LineItemEditorLedger;
type LineItemDraft = SharedLineItemDraft;
const blankDraft = blankLineItemDraft;
const LineItemEditor = SharedLineItemEditor;

type EditingState =
  | { classCode: string; mode: 'add'; draft: LineItemDraft }
  | { classCode: string; mode: 'edit'; localId: string; draft: LineItemDraft; renameOnly?: boolean };


// ───── editable cell ────────────────────────────────────────────────────────
//
// Behaviour matrix:
//
//                            displayMode = 'actual'        displayMode = 'rounded'
//   onActualChange undefined  read-only span                editable input (rounded)
//   onActualChange defined    editable input (actual)       editable input (rounded)
//
// "actual editable" is opt-in via onActualChange — used by MANUAL_DISCLOSURE
// classes (pattern 3) and manual line_items, where the actual value is
// auditor-input rather than TB-derived.

const AmountCell: React.FC<{
  displayMode: DisplayMode;
  actual: number;
  rounded: number;
  autoRounded: number;
  outOfTolerance: boolean;
  readOnly: boolean;
  onRoundedChange: (v: number) => void;
  onActualChange?: (v: number) => void;
  /**
   * When TRUE, display sign is inverted (credit schedules — liability /
   * equity / income). Stored values stay signed; presentation flips at
   * the cell boundary. Auditor sees positive numbers and edits with
   * positive numbers; the underlying store remains signed-negative.
   */
  invertSign?: boolean;
  /**
   * FSA-2.4 N2 — Live-by-default override. When provided AND differs
   * from `actual` by more than half-paisa, the cell DISPLAYS this value
   * (with an amber tint) instead of the saved `actual`. Powers the
   * "show me the latest figures even if I haven't saved" workflow for
   * `ledgers`-mode line items in DRAFT. Only affects 'actual' display
   * mode; 'rounded' continues to show auditor-entered rounded value.
   * Pass `undefined` for cells with no live notion (manual / cross-note /
   * pl_flow line items, MANUAL_DISCLOSURE classes, computed cells).
   */
  liveActual?: number;
}> = ({
  displayMode,
  actual,
  rounded,
  autoRounded,
  outOfTolerance,
  readOnly,
  onRoundedChange,
  onActualChange,
  invertSign,
  liveActual,
}) => {
  // Single transform boundary: flip OUT on read, flip IN on write.
  const sign = invertSign ? -1 : 1;
  // FSA-2.4 N2 — live override takes precedence over saved when defined
  // and meaningfully different (half-paisa floating-point safety).
  const hasLiveOverride =
    liveActual !== undefined && Math.abs(liveActual - actual) > 0.005;
  const displayActual = (hasLiveOverride ? (liveActual as number) : actual) * sign;
  const displayRounded = rounded * sign;
  const displayAutoRounded = autoRounded * sign;
  const wrappedOnRoundedChange = (n: number) => onRoundedChange(n * sign);
  const wrappedOnActualChange = onActualChange
    ? (n: number) => onActualChange(n * sign)
    : undefined;

  if (displayMode === 'actual') {
    if (wrappedOnActualChange && !readOnly) {
      return (
        <NumericInput
          className="form-input"
          value={displayActual}
          onChange={wrappedOnActualChange}
          style={amountInputStyle}
        />
      );
    }
    return (
      <span
        style={{
          fontVariantNumeric: 'tabular-nums',
          ...(hasLiveOverride ? liveOverrideCellStyle : null),
        }}
        title={hasLiveOverride
          ? `Live ledger sum (saved: ${fmtAmt(actual * sign)}). Click Save to persist.`
          : undefined}
      >
        {fmtAmt(displayActual)}
      </span>
    );
  }
  return (
    <NumericInput
      className="form-input"
      value={displayRounded}
      readOnly={readOnly}
      disabled={readOnly}
      onChange={wrappedOnRoundedChange}
      style={{
        ...amountInputStyle,
        outline: outOfTolerance ? '1px solid #f59e0b' : undefined,
        background: readOnly
          ? 'var(--bg-secondary)'
          : (hasLiveOverride ? '#fef3c7' : undefined),
      }}
      title={
        hasLiveOverride
          ? `Live ledger differs from saved rounded — switch to Actual mode to see the live figure (saved actual: ${fmtAmt(actual * sign)}).`
          : (outOfTolerance
              ? `Outside tolerance (auto = ${fmtAmt(displayAutoRounded)})`
              : `Auto = ${fmtAmt(displayAutoRounded)}`)
      }
    />
  );
};

// FSA-2.4 N2 — amber tint applied to actual-mode cells where the live
// ledger sum differs from the saved snapshot. Subtle so the auditor
// reads the figure first, the "this is live" cue second.
const liveOverrideCellStyle: React.CSSProperties = {
  background: '#fef3c7',
  borderRadius: 3,
  padding: '1px 6px',
  display: 'inline-block',
};

// ───── styles ────────────────────────────────────────────────────────────────

const fmtAmt = (v: number) =>
  v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const paneStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
};
const toolbarStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '0.5rem 0.75rem',
  borderBottom: '1px solid var(--border-color)',
  background: 'var(--bg-secondary)',
  flexWrap: 'wrap',
};
const toggleGroupStyle: React.CSSProperties = {
  display: 'inline-flex',
  border: '1px solid var(--form-border-color)',
  borderRadius: 6,
  overflow: 'hidden',
};
const toggleBtnStyle = (active: boolean): React.CSSProperties => ({
  border: 'none',
  background: active ? '#2563eb' : 'var(--bg-primary)',
  color: active ? 'white' : 'var(--text-primary)',
  padding: '4px 10px',
  fontSize: '0.78rem',
  cursor: 'pointer',
  fontWeight: 600,
});
const pillStyle = (bg: string, color: string): React.CSSProperties => ({
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: 999,
  background: bg,
  color,
  fontSize: '0.7rem',
  fontWeight: 600,
});

const emptyClassesHintStyle: React.CSSProperties = {
  padding: '40px 24px',
  textAlign: 'center',
  background: 'var(--bg-secondary)',
  border: '1px dashed var(--border-color)',
  borderRadius: 8,
  marginTop: 8,
};

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.85rem',
};
const theadStyle: React.CSSProperties = { position: 'sticky', top: 0, zIndex: 1 };
const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '8px 12px',
  background: 'var(--grid-header-bg, #dbeafe)',
  borderBottom: '1px solid var(--border-color)',
  fontSize: '0.7rem',
  fontWeight: 700,
  letterSpacing: 0.4,
  color: 'var(--text-secondary)',
};
const tdStyle: React.CSSProperties = {
  padding: '8px 12px',
  borderBottom: '1px solid var(--border-color)',
};
const totalRowStyle: React.CSSProperties = { background: 'var(--bg-secondary)' };
const expandedRowStyle: React.CSSProperties = {
  background: 'var(--bg-secondary)',
  borderBottom: '1px solid var(--border-color)',
  padding: 0,
};

// ───── class / line item row styles (compact, no expansion) ────────────────

const classRowStyle: React.CSSProperties = {
  background: 'var(--bg-primary)',
};
const classNameRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexWrap: 'wrap',
};
const classMetaStyle: React.CSSProperties = {
  fontSize: '0.68rem',
  color: 'var(--text-secondary)',
  marginTop: 1,
};
const inlineAddBtnStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 3,
  background: 'transparent',
  border: '1px dashed #93c5fd',
  borderRadius: 4,
  padding: '1px 6px',
  fontSize: '0.68rem',
  color: '#1d4ed8',
  cursor: 'pointer',
  fontWeight: 600,
};
const lineItemRowStyle: React.CSSProperties = {
  background: 'var(--bg-secondary)',
};
const lineItemNameTdStyle: React.CSSProperties = {
  padding: '6px 12px 6px 32px',
  borderBottom: '1px dashed var(--border-color)',
  fontSize: '0.82rem',
};
const lineItemIndentStyle: React.CSSProperties = {
  color: 'var(--text-secondary)',
  marginRight: 6,
  fontWeight: 400,
};
const lineItemModeChipStyle: React.CSSProperties = {
  marginLeft: 8,
  fontSize: '0.62rem',
  color: 'var(--text-secondary)',
  textTransform: 'uppercase',
  letterSpacing: 0.3,
  fontWeight: 600,
};
// FSA-2.4 N1 — muted drift dot (replaces LedgerDriftChip on line items).
// Amber rather than red — line-level drift is informational, not blocking.
// Hover tooltip carries the amount + remediation.
const lineItemDriftDotStyle: React.CSSProperties = {
  marginLeft: 8,
  color: '#d97706',
  fontSize: '0.7rem',
  cursor: 'help',
  lineHeight: 1,
};
// Class-level dot — same colour as line-item, slightly larger so the
// row scan reads "drift somewhere in this class".
const classDriftDotStyle: React.CSSProperties = {
  marginLeft: 8,
  color: '#d97706',
  fontSize: '0.9rem',
  cursor: 'help',
  lineHeight: 1,
};
const editorTableRowStyle: React.CSSProperties = {
  background: 'var(--bg-secondary)',
  borderBottom: '1px solid var(--border-color)',
  padding: 0,
};
const subHeadingStyle: React.CSSProperties = {
  fontSize: '0.72rem',
  fontWeight: 700,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  color: 'var(--text-secondary)',
  margin: '6px 0',
};
const subTdStyle: React.CSSProperties = {
  padding: '4px 12px',
  borderBottom: '1px dashed var(--border-color)',
};
const amountInputStyle: React.CSSProperties = {
  fontSize: '0.85rem',
  padding: '0.25rem 0.5rem',
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
  width: 130,
};
const chevronBtnStyle: React.CSSProperties = {
  padding: '2px',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  color: 'var(--text-secondary)',
};

// ───── line item editor styles ──────────────────────────────────────────────

const editorContainerStyle: React.CSSProperties = {
  marginTop: 10,
  padding: 12,
  border: '1px solid var(--form-border-color)',
  borderRadius: 6,
  background: 'var(--bg-primary)',
  maxWidth: 560,
};
const editorTitleStyle: React.CSSProperties = {
  fontSize: '0.82rem',
  fontWeight: 700,
  color: 'var(--text-primary)',
  marginBottom: 8,
};
const editorRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  marginBottom: 6,
};
const editorLabelStyle: React.CSSProperties = {
  width: 90,
  fontSize: '0.78rem',
  color: 'var(--text-secondary)',
  fontWeight: 500,
};

// FSA-2.2 multi-ledger line items: scrollable checkbox list for the
// ledger-mode picker. Caps at ~10 rows visible before scrolling.
const ledgerPickerStyle: React.CSSProperties = {
  flex: 1,
  maxHeight: 220,
  overflowY: 'auto',
  border: '1px solid var(--form-border-color)',
  borderRadius: 4,
  background: 'var(--bg-primary)',
  padding: 4,
  fontSize: '0.8rem',
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
};

// FSA-2.2 Wave 3 incoming-cross-note panel styles. Renders as a sub-row
// under each destination class showing source -> amount list + total.
// Subdued blue tint to distinguish from drift (red) and group headers
// FSA-2.2 Wave 3 (A): incoming cross-note refs render as proper line
// item rows under the target class. These styles decorate the source
// chip (e.g. "from Long-term Borrowings ▸ t1") and the read-only
// amount cells so the auditor can distinguish them from their own
// line items at a glance.
const incomingSourceChipStyle: React.CSSProperties = {
  marginLeft: 8,
  padding: '1px 6px',
  borderRadius: 999,
  background: 'var(--bg-primary)',
  border: '1px solid var(--form-border-color)',
  color: 'var(--text-secondary)',
  fontSize: '0.66rem',
  fontWeight: 600,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
};
const incomingSeparatorStyle: React.CSSProperties = {
  color: 'var(--text-secondary)',
  margin: '0 4px',
};
const incomingReadonlyAmountStyle: React.CSSProperties = {
  fontVariantNumeric: 'tabular-nums',
  fontWeight: 500,
  color: 'var(--text-secondary)',
  fontStyle: 'italic',
};

// FSA-2.2 Wave 2 cross-note editor block — light-yellow tint so the
// auditor visually separates the cross-note portion from the line
// item's own amount inputs.
const crossNoteSectionStyle: React.CSSProperties = {
  marginTop: 8,
  padding: 8,
  borderRadius: 4,
  background: 'var(--bg-secondary)',
  border: '1px dashed var(--form-border-color)',
};

const ledgerPickerSummaryStyle: React.CSSProperties = {
  marginTop: 4,
  marginLeft: 100,
  fontSize: '0.72rem',
  color: 'var(--text-secondary)',
  fontVariantNumeric: 'tabular-nums',
};

// FSA-2.2 chip-multi-select for line item ledgers. Chip box wraps so any
// number of selections fit; the native <select> sits as the trailing add
// affordance. Colors lean on light blue so chips stand out from the
// surrounding form fields without competing with the class-name chips on
// the class row.
// FSA-2.2 sub-head grouping (Wave 1): styles for the group header rows,
// group warning chip (Untagged), and group subtotal rows. Same colour
// palette as the existing class chip strip so the modal stays cohesive.
const groupHeaderRowStyle: React.CSSProperties = {
  background: 'var(--bg-secondary)',
};
const groupHeaderTdStyle: React.CSSProperties = {
  padding: '8px 10px',
  borderBottom: '1px solid var(--form-border-color)',
  borderTop: '2px solid var(--form-border-color)',
};
const groupHeaderLabelStyle: React.CSSProperties = {
  fontWeight: 700,
  fontSize: '0.85rem',
  color: 'var(--text-primary)',
  textTransform: 'uppercase',
  letterSpacing: 0.4,
};
const groupHeaderWarnStyle: React.CSSProperties = {
  display: 'inline-block',
  marginLeft: 10,
  padding: '1px 6px',
  borderRadius: 999,
  background: '#fef3c7',
  color: '#92400e',
  fontSize: '0.7rem',
  fontWeight: 600,
};
const groupSubtotalRowStyle: React.CSSProperties = {
  background: 'var(--bg-primary)',
};
const groupSubtotalTdStyle: React.CSSProperties = {
  padding: '6px 10px',
  fontWeight: 700,
  fontSize: '0.85rem',
  borderTop: '1px solid var(--form-border-color)',
  borderBottom: '2px solid var(--form-border-color)',
};

// FSA-2.2 Wave 1 (#2): per-class subtotal row INSIDE a group (only
// when the class has line items in this group). Lighter visual weight
// than groupSubtotalRowStyle since multiple class subtotals can sit
// inside a single group.
const classSubtotalInGroupRowStyle: React.CSSProperties = {
  background: 'var(--bg-primary)',
};
const classSubtotalInGroupTdStyle: React.CSSProperties = {
  padding: '4px 10px',
  fontWeight: 600,
  fontSize: '0.78rem',
  fontStyle: 'italic',
  color: 'var(--text-secondary)',
  borderBottom: '1px dashed var(--form-border-color)',
};

const ledgerChipBoxStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 6,
  padding: 6,
  border: '1px solid var(--form-border-color)',
  borderRadius: 4,
  minHeight: 36,
  background: 'var(--bg-primary)',
};

const ledgerChipStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '2px 4px 2px 10px',
  borderRadius: 999,
  background: 'var(--bg-secondary)',
  border: '1px solid var(--form-border-color)',
  color: 'var(--text-primary)',
  fontSize: '0.78rem',
  fontWeight: 500,
  maxWidth: 320,
};

const ledgerChipLabelStyle: React.CSSProperties = {
  maxWidth: 180,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const ledgerChipAmountStyle: React.CSSProperties = {
  fontVariantNumeric: 'tabular-nums',
  color: 'var(--text-secondary)',
  fontSize: '0.72rem',
};

const ledgerChipRemoveStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  color: 'var(--text-secondary)',
  fontSize: '1rem',
  lineHeight: 1,
  width: 18,
  height: 18,
  borderRadius: 999,
  padding: 0,
};

const ledgerChipAddSelectStyle: React.CSSProperties = {
  flex: '0 1 auto',
  border: 'none',
  background: 'transparent',
  fontSize: '0.82rem',
  outline: 'none',
  cursor: 'pointer',
  color: 'var(--text-secondary)',
  padding: '2px 6px',
  minWidth: 140,
};

const ledgerChipEmptyStyle: React.CSSProperties = {
  fontSize: '0.78rem',
  color: 'var(--text-secondary)',
  padding: '4px 6px',
};

const ledgerChipAllPickedStyle: React.CSSProperties = {
  fontSize: '0.72rem',
  color: 'var(--text-secondary)',
  padding: '2px 6px',
};

const ledgerChipSummaryStyle: React.CSSProperties = {
  marginLeft: 4,
  fontSize: '0.72rem',
  color: 'var(--text-secondary)',
  fontVariantNumeric: 'tabular-nums',
};
const editorInputStyle: React.CSSProperties = {
  flex: 1,
  fontSize: '0.82rem',
  padding: '4px 8px',
};
const editorFooterStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 8,
  marginTop: 10,
  paddingTop: 8,
  borderTop: '1px solid var(--border-color)',
};
const radioLabelStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  cursor: 'pointer',
};
const requiredStarStyle: React.CSSProperties = { color: '#dc2626' };
const ledgerHintStyle: React.CSSProperties = {
  color: 'var(--text-secondary)',
  fontSize: '0.7rem',
  fontStyle: 'italic',
};
const emptyHintStyle: React.CSSProperties = {
  fontSize: '0.75rem',
  color: 'var(--text-secondary)',
  fontStyle: 'italic',
  padding: '4px 0 8px 0',
};

// reconBadgeBase / reconOkStyle / reconDriftStyle removed —
// replaced by <ReconciledChip> and <DriftChip> from
// ../shared/RowBadges.

// ───── recon chip + tooltip styles ──────────────────────────────────────────

const toolbarSeparatorStyle: React.CSSProperties = {
  width: 1,
  height: 18,
  background: 'var(--border-color)',
  margin: '0 4px',
};
const reconTooltipStyle: React.CSSProperties = {
  position: 'absolute',
  top: '100%',
  left: 0,
  marginTop: 6,
  minWidth: 360,
  padding: '10px 12px',
  background: 'var(--bg-primary)',
  border: '1px solid var(--border-color)',
  borderRadius: 6,
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
  zIndex: 50,
  fontSize: '0.78rem',
  color: 'var(--text-primary)',
};
const reconTooltipHeaderStyle: React.CSSProperties = {
  fontSize: '0.68rem',
  fontWeight: 700,
  letterSpacing: 0.5,
  textTransform: 'uppercase',
  color: 'var(--text-secondary)',
  marginBottom: 6,
  paddingBottom: 4,
  borderBottom: '1px solid var(--border-color)',
};
const reconTooltipTableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
};
const reconTooltipThStyle: React.CSSProperties = {
  textAlign: 'left',
  fontSize: '0.7rem',
  fontWeight: 600,
  color: 'var(--text-secondary)',
  padding: '2px 6px',
};
const reconTooltipThNumStyle: React.CSSProperties = {
  ...reconTooltipThStyle,
  textAlign: 'right',
  width: 110,
};
const reconTooltipLabelTdStyle: React.CSSProperties = {
  padding: '3px 6px',
  fontWeight: 500,
};
const reconTooltipNumTdStyle: React.CSSProperties = {
  padding: '3px 6px',
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
  fontWeight: 600,
};
const reconTooltipDriftRowStyle: React.CSSProperties = {
  borderTop: '1px solid var(--border-color)',
};
const reconTooltipHintStyle: React.CSSProperties = {
  marginTop: 8,
  paddingTop: 6,
  borderTop: '1px dashed var(--border-color)',
  fontSize: '0.7rem',
  color: 'var(--text-secondary)',
  lineHeight: 1.4,
};

const noteTextBlockStyle: React.CSSProperties = {
  marginTop: 12,
  padding: '10px 12px',
  background: 'var(--bg-secondary)',
  border: '1px solid var(--border-color)',
  borderRadius: 6,
};
const noteTextLabelStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  gap: 8,
  marginBottom: 6,
  fontSize: '0.78rem',
  fontWeight: 600,
  color: 'var(--text-primary)',
};
const noteTextHintStyle: React.CSSProperties = {
  fontSize: '0.7rem',
  fontWeight: 400,
  color: 'var(--text-secondary)',
  fontStyle: 'italic',
};
const noteTextAreaStyle: React.CSSProperties = {
  width: '100%',
  resize: 'vertical',
  minHeight: 60,
  fontSize: '0.85rem',
};

// Inline remark rows — class-level + line-item-level. Distinct row
// background so the auditor sees these as commentary, not data.
const remarkRowStyle: React.CSSProperties = {
  background: 'var(--bg-secondary)',
};
const remarkTdStyle: React.CSSProperties = {
  padding: '6px 10px 8px 18px',
  borderTop: '1px dashed var(--border-color)',
  borderBottom: '1px solid var(--border-color)',
};
const lineItemRemarkTdStyle: React.CSSProperties = {
  padding: '6px 10px 8px 36px',
  borderTop: '1px dashed var(--border-color)',
  borderBottom: '1px solid var(--border-color)',
};
const remarkLabelRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  marginBottom: 4,
};
const remarkLabelStyle: React.CSSProperties = {
  fontSize: '0.7rem',
  fontWeight: 600,
  color: 'var(--text-primary)',
  letterSpacing: 0.2,
};
const remarkHintStyle: React.CSSProperties = {
  fontSize: '0.68rem',
  fontStyle: 'italic',
  color: 'var(--text-secondary)',
};
const remarkTextareaStyle: React.CSSProperties = {
  width: '100%',
  fontSize: '0.78rem',
  padding: '6px 8px',
  resize: 'vertical',
  minHeight: 40,
  lineHeight: 1.4,
};

// Visual state for rows the auditor has flagged hide_in_print = true.
// The row still appears in the modal (so the auditor can review what's
// being hidden + toggle back on), but it's clearly muted to communicate
// "this won't appear in the deliverable PDF".
const hiddenClassRowStyle: React.CSSProperties = {
  opacity: 0.55,
  background: 'repeating-linear-gradient(45deg, transparent, transparent 6px, var(--bg-secondary) 6px, var(--bg-secondary) 12px)',
};
const hiddenBadgeStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 3,
  padding: '1px 6px',
  marginLeft: 6,
  borderRadius: 999,
  background: '#fef3c7',
  color: '#92400e',
  fontSize: '0.62rem',
  fontWeight: 700,
  letterSpacing: 0.3,
  textTransform: 'uppercase',
};

// ledgerDriftBadgeStyle removed — replaced by <LedgerDriftChip>
// from ../shared/RowBadges.

// Renamed-template-label chip — small accent next to the class name
// when a per-engagement label override is active. Hover-title surfaces
// the original template label so the reviewer can cross-check.
const renamedBadgeStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 3,
  padding: '1px 6px',
  marginLeft: 6,
  borderRadius: 999,
  background: '#dcfce7',
  color: '#166534',
  fontSize: '0.62rem',
  fontWeight: 700,
  letterSpacing: 0.3,
  textTransform: 'uppercase',
  cursor: 'help',
};

// customBadgeStyle removed — replaced by <CustomClassChip>
// from ../shared/RowBadges.

// Inline class-label editor styles previously lived here. They were
// migrated to ./shared/ClassLabelEditor.tsx when the multi-column class
// table needed the same editor — the component is now self-contained
// (styles travel with it).

export default MainNotePane;
